/**
 * `/event-interest` — the interest tracker for the 2027 festival season.
 *
 * SEED runs a public interest form built by the `admin-app` team; **EMERGE is
 * the system of record for what comes back.** This page answers the two
 * questions that record exists to answer, and it must answer both:
 *
 *   1. **"Who wants Mumbai"** — demand, by city and by festival.
 *   2. **"What did this institution say"** — the submissions, filterable, with
 *      one institution's seven answers readable in full.
 *
 * 🔴 **AND SINCE 2026-09-20 A THIRD TAB THAT IS NOT ABOUT THE FESTIVAL FORM AT
 * ALL — B2B PROPOSAL REQUESTS.** The owner asked for them here, as a tab.
 *
 * ⛔ **NOTHING IS SHARED BETWEEN THEM AND NO FIGURE ON ONE MAY BE READ AGAINST
 * THE OTHER.** A proposal request comes off the B2B site's "Get In Touch" form;
 * an interest submission answers named festivals. They are two forms, two
 * records and **two permissions** — `b2b-proposal.read` against
 * `event-interest.read` — and a reader may hold either without the other, which
 * is why the proposals branch is tested AHEAD of this page's own refusal. The
 * gating is written up in full at that mount; the short version is that the pill
 * and the panel both gate on `b2b-proposal`, and the server's `403` stays the
 * guarantee behind them.
 *
 * ⚠️ *SUPERSEDED 2026-09-20: "`ModuleSlug` has no `b2b-proposal` member, so the
 * section mounts UNGATED and the server's `403` is the refusal."* **The member
 * landed** — `src/lib/permissions.ts:521` declares it and `:736` puts it in
 * `OPEN_WRITE_MODULES` — so this page gates on it, and the sentence calling it
 * ungated outlived the code that gates it. ⛔ **The file's own
 * `const B2B_PROPOSAL: ModuleSlug` could not have compiled otherwise**, which is
 * the measurement to reach for rather than this paragraph.
 *
 * ✅ **THE HEADING READS "Enquiries" — owner's decision 2026-09-24**, and the
 * nav row reads `Enquiries Tracker`. ⚠️ *SUPERSEDED: "THE HEADING STILL READS
 * "Event interest", AND IT IS NOT THIS FILE'S TO CHANGE … Flagged, not
 * renamed."* It was flagged because a page named for one form had come to carry
 * a tab about another, and the name is the owner's; the owner renamed it. ⛔
 * **Only the LABEL moved** — the route (`/event-interest`), the module slug
 * (`event-interest`), the `Trackers` eyebrow and every panel title are
 * unchanged, and the refusal copy stays module-specific.
 *
 * ── THE TWO THINGS EVERY FIGURE ON THE DEMAND TAB MUST SAY ──────────────────
 *
 * 🔴 **1. A CITY FIGURE IS EXPLICIT PICKS PLUS WHOLE-TOUR TAKERS.** An
 * `all_cities` respondent stores **no city rows at all** — materialising "all"
 * goes stale the moment a stop changes — so a count of the named picks alone is
 * LOW, and it looks entirely plausible. There is no error and no blank cell; a
 * smaller number just reads as less interest.
 *
 * 🔴 **2. INSTITUTIONS NOBODY HAS APPROVED YET ARE COUNTED SEPARATELY.** A
 * submission attaches to its company immediately, while that company is still
 * unapproved (contract ruling 3), so demand includes institutions nobody has
 * looked at. That is correct — they did express interest — but a single blended
 * number is the one somebody quotes in a meeting.
 *
 * Both sentences live in `src/components/event-interest/event-interest-wording.ts`,
 * in one copy, and both are the documented **fact about the data the reader
 * cannot otherwise see** exception to the no-helper-text prohibition rather
 * than a carve-out from it. ⚠️ **A helper-text sweep must not delete either.**
 *
 * ── THE ADAPTER, AND WHY EVERY WIRE FIELD IS SPELLED EXACTLY ONCE ───────────
 *
 * The components in `src/components/event-interest/` take VIEW MODELS and
 * import no wire type at all. Everything this feature reads off a response is
 * read in ONE marked block below. That is the `projectsOf` idiom this codebase
 * already uses on the UP dashboard, and the reasoning is the same: a field
 * spelled at three call sites is three chances to disagree, and the count
 * fields here are structurally identical — a renderer reading `explicit` where
 * it meant `wholeTour` produces a plausible wrong number with a `200` behind
 * it and nothing to find.
 *
 * ── ONE WRITER PER QUERY KEY ────────────────────────────────────────────────
 *
 * | key          | its ONE writer                                            |
 * | ------------ | --------------------------------------------------------- |
 * | `tab`        | the pill strip                                            |
 * | `projectId`  | the Festival combobox, and the festival table's "See who"  |
 * | `formRegion` | the Region column funnel                                  |
 * | `companyId`  | arrived at BY LINK — a badge, and no control of its own    |
 *
 * ⚠️ **`search` and `page` are LOCAL STATE, not URL keys, matching
 * `/credit-notes` and `/invoices`.** Two writers for one key is the failure
 * this table exists to prevent, and the reason these two are exempt is that
 * neither is a narrowing somebody shares: a debounced search writing the query
 * string on every keystroke, and a pager restored from a link into a result
 * that has since moved, are both worse than local state. Every key that IS a
 * narrowing somebody would link to is in the URL.
 *
 * ⛔ **THERE IS NO CITY FILTER ON THE SUBMISSIONS LIST, AND ITS ABSENCE IS A
 * DECISION.** Narrowing submissions by city would silently drop every
 * whole-tour taker — the institutions that store no city row — so the list
 * would be short in exactly the way the demand tab spends two sentences
 * warning about, and a list cannot carry that caveat per row. Drilling in is by
 * FESTIVAL, where an answer is a real row either way.
 *
 * ── THE WRITES, AND THEY BELONG TO TWO DIFFERENT RECORDS ────────────────────
 *
 * ⚠️ *SUPERSEDED: "── **ONE WRITE, AND IT IS A DELETE** ──".* There are **five**,
 * across two resources that share nothing but this page:
 *
 *   - a SUBMISSION can be **deleted** from its detail dialog, by an
 *     administrator;
 *   - a PROPOSAL ENQUIRY can be **archived**, **unarchived**, **converted to a
 *     deal** and **deleted** — wired at the panel's mount, gated in the panel,
 *     and on **three different tiers** which nothing here harmonises.
 *
 * ⛔ **DO NOT COUNT THEM FROM THIS PARAGRAPH.** A count of anything somebody can
 * add is the sentence that rots first; the four proposal handlers are together
 * under one banner below and the submission's is at `DeleteSubmissionControl`.
 *
 * ⚠️ *SUPERSEDED, earlier still: "⛔ **No create, no edit, no delete, and none
 * may be added here** ... Nothing on this page takes a callback that could
 * write."*
 *
 * ⚠️ **No create and no edit, still.** A submission is the record of what a
 * customer said: deciding it should not have been recorded is a judgement
 * somebody may make, and rewriting what they said is not. Ruling 4 is
 * untouched — a rejected company's submissions survive, and nothing deletes one
 * on their behalf.
 *
 * 🔴 **THE DELETE MOVES BOTH TABS, WHICH IS WHY IT RELOADS BOTH.** It takes the
 * company off every project of interest the submission's `yes` answers named,
 * so the DEMAND report is wrong the moment it lands — and that read fires once
 * on mount. Without the second reload, a reader who deletes on the submissions
 * tab and switches to Demand reads pre-delete figures with nothing on screen
 * saying so. `loadDemand` is lifted out of its effect for exactly that.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronIcon, SearchIcon } from '@/components/catalog/icons';
/*
 * The "create a company for this enquiry" modal — ONE mount at this page,
 * shared by the proposals and report-lead sections, never one per row.
 */
import { CreateCompanyForEnquiry } from '@/components/enquiries/CreateCompanyForEnquiry';
/*
 * The "create a contact for this enquiry" flow (owner, 2026-10-02) — ONE mount
 * at this page, shared by all three tabs, for the same reason as the above.
 */
import {
  CreateContactForEnquiry,
  type EnquiryContactRequest,
} from '@/components/enquiries/CreateContactForEnquiry';
import {
  ENQUIRY_ACTIONS_COLUMN_WIDTH,
  EnquiryRowActions,
} from '@/components/enquiries/EnquiryRowActions';
import { AssignEnquiryModal } from '@/components/enquiries/AssignEnquiryModal';
import { EnquiryAssignmentCell } from '@/components/enquiries/EnquiryAssignmentCell';
import {
  DeleteSubmissionControl,
  DemandPanel,
  EventInterestIcon,
  FestivalDemandPanel,
  SubmissionDetailModal,
  FORM_REGION_OPTIONS,
  canDeleteSubmissions,
  formRegionLabel,
  formRegionLabelOf,
  type DemandCityRow,
  type DemandFestivalRow,
  type RemovedProject,
  type SubmissionDetail,
  type SubmissionFestivalAnswer,
} from '@/components/event-interest';
/*
 * ⚠️ **IMPORTED BY PATH, NOT THROUGH THE BARREL ABOVE.** This panel and this
 * page were written in the same hour by two people, and `index.ts` is a third
 * file neither of them owns — so a barrel import couples this mount to an
 * export nobody here can add. The deep path is the smaller dependency, and it
 * fails LOUDLY (a `tsc` error naming the module) rather than as a re-export
 * somebody forgot.
 */
import { ProposalsPanel, type ProposalRowAction } from '@/components/event-interest/ProposalsPanel';
import {
  ASSIGNEE_PARAM,
  ENQUIRY_ASSIGNMENT_COLUMN_WIDTH,
  assigneeFilterOf,
  assigneeFilterOptions,
  assignmentColumnSpec,
} from '@/components/enquiries/enquiry-assignment-filter';
/*
 * ⚠️ **THE SAME DEEP PATH, FOR THE SAME REASON AS THE PANEL ABOVE**, and one
 * more: `proposalIsConvertible` is exported from this module precisely so the
 * list that offers the control and the flow that runs it cannot disagree about
 * whether an enquiry has already been converted. Importing the pair from ONE
 * module is what keeps that true — a barrel could re-export one and not the
 * other, and a second copy of that test is the defect it was exported to
 * prevent.
 */
import {
  ProposalConvertFlow,
  proposalIsConvertible,
  type ProposalDealLinkOutcome,
} from '@/components/event-interest/ProposalConvertFlow';
/* The report-download section, imported by the same deep path for the same reasons. */
import {
  ReportDownloadsPanel,
  type ReportDownloadRowAction,
} from '@/components/event-interest/ReportDownloadsPanel';
import {
  ReportDownloadConvertFlow,
  reportLeadIsConvertible,
} from '@/components/event-interest/ReportDownloadConvertFlow';
import {
  Badge,
  Button,
  ColumnManager,
  MultiCombobox,
  EmptyState,
  Field,
  FilterableTH,
  FilterSheet,
  Input,
  PageHeader,
  Panel,
  PanelFooter,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type ColumnFilterOption,
  type ComboboxOption,
} from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { splitFullName } from '@/lib/contact-name';
import { cx } from '@/lib/cx';
import { formatDateTime } from '@/lib/format';
import { denialReason, type ModuleSlug } from '@/lib/permissions';
import type {
  B2bProposalSubmission,
  CompanyApprovalStatus,
  EnquiryAssignment,
  EnquirySource,
  EventInterestDemand,
  EventInterestDemandFestival,
  EventInterestDemandRow,
  EventInterestFormRegion,
  EventInterestListParams,
  EventInterestSubmission,
  Paginated,
  ReportDownloadLead,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { useEnquirySummary } from '@/lib/use-enquiry-summary';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { CountBadge } from '@/components/ui/CountBadge';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import { usePageLayout } from '@/lib/use-page-layout';

/**
 * The contact form's name seed from an enquiry, which carries the person as ONE
 * string: `splitFullName`'s guess for First / Last name and the string as typed
 * in Full name (lib/contact-name.ts). The three start IN SYNC (the split
 * round-trips), so the form's two-way `applyNameEdit` carries every later edit
 * across all three; the person sees and can correct them before saving.
 */
function contactNamePrefill(fullName: string): { name: string; firstName: string; lastName: string } {
  return { name: fullName.trim(), ...splitFullName(fullName) };
}

/**
 * 🔴 **A REAL MODULE GATED ON PERMISSION SETS, NOT A `NAV_SURFACE_MODULE` VIEW
 * CONTROL** — contract §3 says so in as many words, and the difference decides
 * what a refused reader meets.
 *
 * A nav surface hides a page this app draws while the API behind it answers
 * everybody the same; *hiding a page is not refusing a request*. Here the grant
 * IS the refusal, so a reader without it meets a real refusal panel naming what
 * they may do, rather than an empty table that reads as "nobody has submitted".
 *
 * ⛔ **AND IT MUST NEVER JOIN `NAV_SURFACE_MODULE`** — that set forces
 * `create`/`update`/`delete` to a hard `false`, which would be this app
 * inventing refusals for a module whose writes are the form's, not a reader's.
 *
 * ⚠️ **The slug is declared `ModuleSlug` deliberately rather than passed as a
 * bare string.** If `event-interest` has not landed in that union, this line is
 * a one-line `tsc` error here — which is the loud failure. ⛔ **Do not "fix" it
 * by borrowing a neighbouring module's slug**: that is the
 * `integrations`-borrowing-`companies` mistake, where a grant for one
 * capability silently carries a second nobody was asked about.
 */
const EVENT_INTEREST: ModuleSlug = 'event-interest';

/**
 * 🔴 **THE PROPOSALS SECTION IS A DIFFERENT MODULE ON THE SAME PAGE.**
 *
 * The owner chose *"a tab on `/event-interest`, as asked"* with the cost
 * stated, and this is that cost: **one page, two modules' grants.** Migration
 * 219 grants `event-interest.read` to `university-partnerships` AND
 * `operations`; migration 230 grants `b2b-proposal.read` to
 * `university-partnerships` alone — so the two genuinely diverge, and the pill
 * must ask its own question rather than ride on {@link EVENT_INTEREST}.
 *
 * ⛔ **NOT a borrowed slug.** Reusing `event-interest` here is the
 * `integrations`-borrowing-`companies` mistake the constant above names: a
 * grant for one capability silently carrying a second nobody was asked about.
 *
 * ⚠️ **The separating population is EMPTY today** — `operations` maps to
 * `viewer`, which has held nobody since 2026-09-15 — so neither branch can be
 * driven until somebody is put back on that role. **Re-measure the role
 * distribution; do not read the absence of complaints as correctness.**
 */
const B2B_PROPOSAL: ModuleSlug = 'b2b-proposal';

/**
 * 🔴 **A THIRD MODULE ON THE SAME PAGE** — the report-download lead log, gated
 * on its own grant exactly as {@link B2B_PROPOSAL} is and for the same reasons.
 * ⛔ Not a borrowed slug, and the pill asks its own question.
 */
const REPORT_DOWNLOAD: ModuleSlug = 'report-download';

/** The API's ceiling (1..100) — owner's decision 2026-10-02, up from 20. */
const PER_PAGE = 100;
/** Fewer than a full page — enough to read as a list, not a wall of shimmer. */
const SKELETON_ROWS = 6;

/* -------------------------------------------------------------------------- *
 * The tabs
 * -------------------------------------------------------------------------- */

type Tab = 'demand' | 'submissions' | 'proposals' | 'reports';

const TABS: readonly { value: Tab; label: string }[] = [
  { value: 'demand', label: 'Demand' },
  /* The `?tab=` value stays `submissions`; only the label moved (owner, 2026-09-27). */
  { value: 'submissions', label: 'Event Interest Submissions' },
  /**
   * 🔴 **A DIFFERENT FORM, A DIFFERENT RECORD, AND A DIFFERENT PERMISSION.**
   * The B2B "Get In Touch" form is not the 2027 festival interest form; what it
   * produces is a request for a proposal, not an answer about a festival. The
   * owner asked for it on this page as a tab, so it is one — but nothing about
   * the two sections' data is shared, and no figure on either may be read
   * against the other.
   *
   * ⚠️ **It reads `b2b-proposal.read`, NOT `event-interest.read`** — see the
   * mount below, where the gating is written up in full. ⚠️ *SUPERSEDED
   * 2026-09-20: "where the absence of a slug for it is written up in full."*
   * **There is a slug**, and this pill is withheld from a reader without it.
   */
  { value: 'proposals', label: 'Proposals' },
  /**
   * The gated-report lead log. A different record and a different permission
   * again — `report-download.read` — withheld from a reader without it, and a
   * URL arrival gets a refusal naming that module. It owns no query key, so it
   * adds nothing to {@link CLEARED_ON_SWITCH}.
   */
  // "Report leads", not "downloads" (owner's decision 2026-09-25): the PDF is
  // also reachable without the form, so a row is someone who FILLED THE FORM,
  // never a log of every download.
  { value: 'reports', label: 'Report leads' },
];

const TAB_PARAM = 'tab';

/**
 * The tab, or the default.
 *
 * ⛔ **A POSITIVE TEST PER MEMBER WITH A FALLBACK, NEVER A NEGATION.** Written
 * as `raw === 'demand' ? 'demand' : …` — or as any other negation — every stale
 * link, every typo and every value a future tab has not been taught about lands
 * on whichever branch the negation happens to name, rather than on the default.
 * `/sales-tracker` records exactly that hazard: read as a negation, its dropped
 * `?view=board` links would have landed on the sheet mirror instead of the
 * table, silently.
 *
 * ⚠️ **AND AN UNRECOGNISED VALUE MUST NOT BLANK THE PAGE.** A tracker rendering
 * nothing because somebody pasted a bad query string is indistinguishable from
 * one that failed to load.
 */
function parseTab(raw: string | null): Tab {
  if (raw === 'submissions') return 'submissions';
  if (raw === 'proposals') return 'proposals';
  if (raw === 'reports') return 'reports';
  return 'demand';
}

/**
 * 🔴 **EVERY QUERY KEY ANY TAB OWNS, CLEARED ON EVERY SWITCH.**
 *
 * Every tab lives in ONE query string, so a key left behind applies one tab's
 * narrowing to another's content — a filtered table under an unfiltered
 * heading, which is this codebase's most repeated failure.
 *
 * ⚠️ **THE PROPOSALS TAB ADDS NOTHING TO THIS LIST BECAUSE IT OWNS NO QUERY
 * KEY — measured, not assumed: `useSearchParams` appears nowhere in
 * `src/components/event-interest/` (2026-09-20).** Its narrowings are local
 * state, so there is nothing of its own to leak in either direction. ⛔ **The
 * moment it takes one — a status, a date bound, a pager — that key belongs
 * here**, or a filter set on Proposals survives a switch to Submissions and
 * narrows a list whose own controls do not show it. A key added to that panel
 * and not to this list is silent in both directions. **Re-run the grep; do not
 * inherit this sentence.**
 *
 * ⚠️ **`projectId` is the one that matters**: it means "this festival" on both
 * tabs, and carrying it across is arguably useful — but it would be a filter
 * nobody set, applied to a report whose own controls do not show it. The
 * festival table's **See who** button is the deliberate exception, and it
 * carries the id across in the SAME `URLSearchParams` as the tab change rather
 * than as a second write.
 */
const CLEARED_ON_SWITCH = ['projectId', 'formRegion', 'companyId', 'approvalStatus'] as const;

/* -------------------------------------------------------------------------- *
 * Reading the query string
 * -------------------------------------------------------------------------- */

/**
 * A positive integer, or nothing.
 *
 * Blank and malformed values are DROPPED rather than forwarded: `?projectId=`
 * is a 422 on a `.strict()` schema, not "no filter", and a pasted
 * `?projectId=abc` would take the whole list down — which renders as an empty
 * table and reads as "nobody wants this festival".
 */
function parseId(raw: string | null): number | null {
  if (!raw) return null;
  return /^[1-9]\d*$/.test(raw) ? Number(raw) : null;
}

/**
 * The form region, or `''` for no filter.
 *
 * ⚠️ **VALIDATED AGAINST THE TWO KNOWN VALUES rather than forwarded**, because
 * `form_region` is a `VARCHAR` checked in zod — an unrecognised value is a 422
 * that empties the table, which reads as "nothing matches".
 *
 * 🔴 **`north-america` HYPHENATES, AND `BillingRegion` CARRIES `north_america`
 * WITH AN UNDERSCORE ONE CHARACTER AWAY.** Both mean North America, neither is
 * wrong, and neither can move — the billing one is a MySQL ENUM whose index is
 * stored, this one is `admin-app`'s wire format. ⛔ **A find-and-replace
 * normalising either silently breaks the other**, and nothing matches across
 * the boundary, so no type can catch it. ⛔ **`eventKey` uses UNDERSCORES while
 * this does not** — also correct, also not to be harmonised.
 *
 * ⚠️ **A stale spelling IS caught here**, because the narrowed value is
 * returned: `raw === 'north_america'` narrows `raw` to a literal that is not
 * assignable to `EventInterestFormRegion`, which is a compile error rather than
 * a filter that 422s at runtime.
 */
function parseRegion(raw: string | null): EventInterestFormRegion | '' {
  return raw === 'north-america' || raw === 'global' ? raw : '';
}

/**
 * The company's approval state, or `''` for no filter.
 *
 * ⚠️ **THE COMPANY'S, NOT THE SUBMISSION'S — a submission has no approval
 * state of its own.** This is how the unapproved half is isolated on the LIST,
 * the same separation the demand report makes with its own column.
 *
 * ⚠️ **`rejected` is offered like any other.** A rejected company's submissions
 * survive (contract ruling 4) and are a real population somebody may want to
 * read; hiding the option would make them reachable by nothing.
 */
function parseApprovalStatus(raw: string | null): CompanyApprovalStatus | '' {
  return raw === 'approved' || raw === 'pending' || raw === 'rejected' ? raw : '';
}

/*
 * ⚠️ **MULTI-VALUE FILTERS (owner 2026-10-28).** Every id/enum key here takes a
 * comma-separated list in the SAME param, so `?projectId=4` from an old
 * bookmark is still a one-item list. Each part is validated by the single-value
 * parser above and malformed parts are DROPPED, never forwarded — one bad part
 * would 422 the whole list. Duplicates collapse; order is the URL's.
 */
function parseList<V>(raw: string | null, parseOne: (part: string) => V | null): V[] {
  if (!raw) return [];
  const out: V[] = [];
  for (const part of raw.split(',')) {
    const value = parseOne(part.trim());
    if (value !== null && !out.includes(value)) out.push(value);
  }
  return out;
}

const parseIdList = (raw: string | null): number[] => parseList(raw, parseId);
const parseRegionList = (raw: string | null): EventInterestFormRegion[] =>
  parseList(raw, (part) => {
    const region = parseRegion(part);
    return region === '' ? null : region;
  });
const parseApprovalStatusList = (raw: string | null): CompanyApprovalStatus[] =>
  parseList(raw, (part) => {
    const status = parseApprovalStatus(part);
    return status === '' ? null : status;
  });

const APPROVAL_FILTER_OPTIONS: ComboboxOption[] = [
  { value: 'approved', label: 'Approved' },
  { value: 'pending', label: 'Awaiting approval' },
  { value: 'rejected', label: 'Rejected' },
];

/* ========================================================================== *
 * THE ADAPTER — THE ONLY PLACE THIS FEATURE READS A WIRE FIELD
 *
 * ⚠️ **EVERY FIELD NAME BELOW IS READ OFF THE RESPONSE EXACTLY ONCE.** The
 * components take view models and import no wire type, so if a name here is
 * wrong the failure is a `tsc` error in this block rather than a plausible
 * wrong number rendered in six places.
 *
 * ⛔ **NOTHING HERE COMPUTES A DEMAND FIGURE.** Every count is a field on the
 * row. One institution wanting three cities is three rows, so anything summed
 * across them counts it several times — and the result looks exactly like a
 * number of institutions.
 * ========================================================================== */

/**
 * A city row from the demand report.
 *
 * ⛔ **`totalCompanies` IS READ, NEVER RE-DERIVED BY ADDING THE PAIR.** The type
 * says so at the field: the point of carrying `explicitCompanies` and
 * `allCitiesCompanies` separately is that either one ALONE is the under-count
 * this feature exists to avoid, and a browser-side sum is a second answer to a
 * figure the server already stated.
 *
 * ⛔ **AND NOTHING DERIVES AN "APPROVED" FIGURE EITHER.** The wire carries
 * `unapprovedCompanies` and not its complement, deliberately: §8 requires the
 * UNAPPROVED half to be visible while the headline stays inclusive. A
 * `total − unapproved` column would be the only number on this report that no
 * server ever stated, sitting between two that were.
 *
 * ⚠️ **EVERY FIELD BELOW IS COPIED, NOT COMPUTED.** That is the property worth
 * keeping: if a figure on the demand tab is wrong, it is wrong on the wire, and
 * there is no arithmetic here for it to have been wrong in.
 */
function toDemandCityRow(row: EventInterestDemandRow): DemandCityRow {
  return {
    key: `${row.project.id}:${row.city.id}`,
    festivalId: row.project.id,
    festivalName: row.project.name,
    cityId: row.city.id,
    cityName: row.city.name,
    explicit: row.explicitCompanies,
    wholeTour: row.allCitiesCompanies,
    institutions: row.totalCompanies,
    notApproved: row.unapprovedCompanies,
  };
}

/**
 * A festival row from the demand report.
 *
 * ⛔ **IT TAKES `EventInterestDemandFestival`, A DIFFERENT TYPE FROM THE CITY
 * ROW, AND THAT IS THE WHOLE REASON IT EXISTS.** An institution that named six
 * stops is six city rows and **one** institution wanting the festival, so a
 * per-festival figure added up from the city table is larger than the truth —
 * plausibly, and by a factor nobody can spot. The server counts DISTINCT
 * institutions; nothing here may substitute a sum.
 *
 * ⚠️ **`unapprovedCompanies` IS A SUBSET OF `interestedCompanies` HERE — of the
 * YESES, not of the row.** It is the same key name the city row carries against
 * a different denominator, which is exactly the kind of near-identity that
 * makes the two types worth keeping apart.
 */
function toDemandFestivalRow(row: EventInterestDemandFestival): DemandFestivalRow {
  return {
    key: String(row.project.id),
    festivalId: row.project.id,
    festivalName: row.project.name,
    yes: row.interestedCompanies,
    no: row.declinedCompanies,
    travelPackage: row.travelPackageCompanies,
    wholeTour: row.allCitiesCompanies,
    notApproved: row.unapprovedCompanies,
  };
}

/**
 * A submission, as the LIST renders it.
 *
 * ⚠️ **`answered` and `saidYes` are counted from THIS submission's own event
 * rows, which is safe** — it is a count of festivals on one record, not a count
 * of institutions across records. That distinction is the whole of why nothing
 * else on this page is summed client-side.
 */
function toSubmissionRow(submission: EventInterestSubmission): SubmissionRow {
  const answered = submission.events.length;
  let saidYes = 0;
  for (const event of submission.events) if (event.response === 'yes') saidYes += 1;
  return {
    id: submission.id,
    companyId: submission.company.id,
    companyName: submission.company.name,
    approvalStatus: submission.company.approvalStatus,
    contactName: submission.contactName,
    contactEmail: submission.contactEmail,
    contactDesignation: submission.contactDesignation,
    contactPhone: submission.contactPhone,
    formRegion: submission.formRegion,
    submittedAt: submission.submittedAt,
    answered,
    saidYes,
    dealId: submission.dealId,
    /* ⛔ `typeof`, never `!== null` — an absent key reads `undefined`, and
       `undefined !== null` would file every row of an older server as archived. */
    archived: typeof submission.archivedAt === 'string',
    assignment: submission.assignment,
  };
}

/** A submission, as the DIALOG renders it. */
function toSubmissionDetail(submission: EventInterestSubmission): SubmissionDetail {
  return {
    id: submission.id,
    companyName: submission.company.name,
    approvalStatus: submission.company.approvalStatus,
    contactName: submission.contactName,
    contactEmail: submission.contactEmail,
    contactDesignation: submission.contactDesignation,
    contactPhone: submission.contactPhone,
    formRegion: submission.formRegion,
    sourceReference: submission.sourceReference,
    submittedAt: submission.submittedAt,
    consentText: submission.consentText,
    consentVersion: submission.consentVersion,
    consentedAt: submission.consentedAt,
    dealId: submission.dealId,
    /*
     * ⚠️ **CARRIED BECAUSE `dealId` ALONE CANNOT SAY WHICH OF THREE THINGS
     * HAPPENED.** `null` + `null` is *never raised, or it failed*; `null` + a
     * stamp is *created and since deleted*, which is ordinary. Dropping this
     * from the view model would fold a silent failure into a routine one.
     */
    dealCreatedAt: submission.dealCreatedAt,
    pursuitsCreatedAt: submission.pursuitsCreatedAt,
    /*
     * ⛔ **`typedCountryName` IS DELIBERATELY NOT CARRIED ONTO THIS SCREEN.**
     * Its own type says to render it "only where a reader is deciding what the
     * company's country should be" — which is the approval screen, not a
     * read-only tracker where nobody is making that decision. Two surfaces
     * wording one field differently reads as two facts, and the way to avoid
     * that here is to leave it to the page that owns the decision. **If it is
     * ever wanted, take the approval screen's wording verbatim rather than
     * writing a second one.**
     */
    festivals: submission.events.map(
      (event): SubmissionFestivalAnswer => ({
        key: String(event.projectId),
        festivalName: event.projectName,
        response: event.response,
        inclusion: event.inclusion,
        selection: event.selection,
        /*
         * 🔴 **THE UNION IS NARROWED HERE, AND `cities` DOES NOT COMPILE UNTIL
         * IT IS.** Two of the three arms carry no `cities` key at all, which is
         * the type stopping the defect it was shaped to stop: an `all_cities`
         * answer and a single-location festival both arrive with an empty city
         * list on the wire, and `length === 0` reads as *"they chose nothing"*
         * on the answer that means **they chose everything**.
         *
         * ⚠️ **The `[]` on the other two arms is SAFE ONLY BECAUSE `selection`
         * TRAVELS WITH IT.** The dialog words an empty list from the SELECTION
         * — `noCitiesReason` — and never from its length, so the two opposite
         * meanings stay apart on the one screen that renders them. ⛔ Nothing
         * downstream may test this array to decide what was asked for.
         */
        cityNames:
          event.selection === 'select_cities'
            ? event.cities.map((choice) => choice.name)
            : [],
        eventKey: event.eventKey,
      }),
    ),
  };
}

/* ========================================================================== *
 * END OF THE ADAPTER
 * ========================================================================== */

/** One row of the submissions table. */
interface SubmissionRow {
  id: number;
  /** Every submission is attached to a company at ingest, so this is never null. */
  companyId: number;
  companyName: string;
  approvalStatus: EventInterestSubmission['company']['approvalStatus'];
  contactName: string;
  contactEmail: string;
  /** Carried only to prefill "Create a contact" (2026-10-02); no cell renders them. */
  contactDesignation: EventInterestSubmission['contactDesignation'];
  contactPhone: EventInterestSubmission['contactPhone'];
  /**
   * ⚠️ **TYPED TO THE UNION, not to `string`** — a stale spelling in a cell or
   * a label map is then a compile error rather than a slug rendered as a word.
   */
  formRegion: EventInterestFormRegion;
  submittedAt: string;
  /** Festivals this submission answered at all — yes AND no. */
  answered: number;
  saidYes: number;
  /**
   * The deal the server raised at ingest, or `null`. ⚠️ `null` is two facts
   * (never raised; raised and since deleted), so it is carried to WORD the row
   * only — the Deal slot renders a link when it is set and nothing when not.
   */
  dealId: number | null;
  archived: boolean;
  /**
   * The latest assignment. `null` is unassigned; `undefined` is an older server
   * that does not report the field — the cell renders a dash, never "Unassigned".
   */
  assignment: EnquiryAssignment | null | undefined;
}

/* -------------------------------------------------------------------------- *
 * The submissions views — Live / Archived / All
 * -------------------------------------------------------------------------- */

/**
 * Which population the submissions list is showing. Mirrors `ProposalsPanel`:
 * **Live** sends `archived: false`, **Archived** sends `archived: true`, and
 * **All OMITS THE KEY**, because the server reads an absent key as everything.
 *
 * ⚠️ **Local state, not a URL key**, for the reason the proposals panel gives:
 * this page already owns `?tab=`, and a second key would have to join
 * `CLEARED_ON_SWITCH` or narrow a sibling section. Accepted cost: the view is
 * not shareable and does not survive a reload.
 */
type SubmissionView = 'live' | 'archived' | 'all';

interface SubmissionViewSpec {
  value: SubmissionView;
  label: string;
  singular: string;
  plural: string;
  emptyTitle: string;
  emptyDescription: string;
}

const LIVE_SUBMISSION_VIEW: SubmissionViewSpec = {
  value: 'live',
  label: 'Live',
  singular: 'live submission',
  plural: 'live submissions',
  emptyTitle: 'No live event interest submissions',
  /* An empty working queue has two causes; it names the archive so a reader
     who has just archived the last row is not told the log is empty. */
  emptyDescription:
    'Nothing is waiting here. Archived submissions are on the Archived view, and new ones appear as institutions answer the interest form.',
};

const SUBMISSION_VIEWS: readonly SubmissionViewSpec[] = [
  LIVE_SUBMISSION_VIEW,
  {
    value: 'archived',
    label: 'Archived',
    singular: 'archived submission',
    plural: 'archived submissions',
    emptyTitle: 'Nothing is archived',
    emptyDescription: 'No event interest submission has been archived. Archiving one takes it off the Live view.',
  },
  {
    value: 'all',
    label: 'All',
    singular: 'submission',
    plural: 'submissions',
    emptyTitle: 'No event interest submissions yet',
    emptyDescription: 'Nobody has answered the interest form yet.',
  },
];

function submissionViewSpecOf(view: SubmissionView): SubmissionViewSpec {
  return SUBMISSION_VIEWS.find((spec) => spec.value === view) ?? LIVE_SUBMISSION_VIEW;
}

/** `all` returns an empty object rather than `{ archived: undefined }` — omit the key. */
function submissionArchivedParamOf(view: SubmissionView): { archived?: boolean } {
  if (view === 'all') return {};
  return { archived: view === 'archived' };
}

/**
 * What the create-company modal is open over: a proposal enquiry or a report
 * lead, with the seed it was opened with. `null` when closed.
 */
type CreatingCompany =
  | { kind: 'proposal'; id: number; seed: { name: string; countryId: number | null } }
  | { kind: 'report'; id: number; seed: { name: string; countryId: number | null } };

/**
 * What the create-contact flow is open over (owner, 2026-10-02). A submission
 * always carries a company, so only `proposal` and `report` can ever link one.
 */
interface CreatingContact {
  kind: 'proposal' | 'report' | 'submission';
  id: number;
  request: EnquiryContactRequest;
}

/** What the assign modal is open over. `null` when closed. */
interface AssigningEnquiry {
  source: EnquirySource;
  id: number;
  /** The name the modal shows: the institution, company or organisation. */
  name: string;
  current: EnquiryAssignment | null;
}

/* -------------------------------------------------------------------------- *
 * The submissions table
 * -------------------------------------------------------------------------- */

/** Who the enquiry is assigned to — FIRST, and code-pinned to the left edge. */
const ASSIGNMENT_COLUMN = 'assignment';
const INSTITUTION_COLUMN = 'institution';
const CONTACT_COLUMN = 'contact';
const REGION_COLUMN = 'formRegion';
const ANSWERED_COLUMN = 'answered';
const SAID_YES_COLUMN = 'saidYes';
const SUBMITTED_COLUMN = 'submittedAt';

type ColumnKey =
  | typeof ASSIGNMENT_COLUMN
  | typeof INSTITUTION_COLUMN
  | typeof CONTACT_COLUMN
  | typeof REGION_COLUMN
  | typeof ANSWERED_COLUMN
  | typeof SAID_YES_COLUMN
  | typeof SUBMITTED_COLUMN;

interface ColumnMeta {
  label: string;
  width: number;
  numeric?: boolean;
}

/*
 * ⚠️ KEYED ON `ColumnKey`, NOT ON `string`. This app builds with
 * `noUncheckedIndexedAccess`, so indexing a `Record<string, …>` yields
 * `ColumnMeta | undefined` at every site below — including the one that sums
 * the table's floor.
 */
const COLUMNS: Record<ColumnKey, ColumnMeta> = {
  [ASSIGNMENT_COLUMN]: { label: 'Assigned', width: ENQUIRY_ASSIGNMENT_COLUMN_WIDTH },
  [INSTITUTION_COLUMN]: { label: 'Institution', width: 260 },
  [CONTACT_COLUMN]: { label: 'Contact', width: 240 },
  [REGION_COLUMN]: { label: 'Form', width: 150 },
  [ANSWERED_COLUMN]: { label: 'Answered', width: 120, numeric: true },
  [SAID_YES_COLUMN]: { label: 'Said yes', width: 110, numeric: true },
  [SUBMITTED_COLUMN]: { label: 'Submitted', width: 170 },
};

const COLUMN_KEYS = [
  ASSIGNMENT_COLUMN,
  INSTITUTION_COLUMN,
  CONTACT_COLUMN,
  REGION_COLUMN,
  ANSWERED_COLUMN,
  SAID_YES_COLUMN,
  SUBMITTED_COLUMN,
] as const;

function isColumnKey(key: string): key is ColumnKey {
  return (COLUMN_KEYS as readonly string[]).includes(key);
}

/** The "See the answers" lane — the desktop table's only route to a submission. */
const ANSWERS_KEY = 'answers';
const ANSWERS_WIDTH = 150;
const ACTIONS_KEY = 'actions';

/** Every column the manager knows, in declared order. Actions last — it is pinned. */
const LAYOUT_KEYS = [...COLUMN_KEYS, ANSWERS_KEY, ACTIONS_KEY] as const;
const COLUMN_LAYOUT_SCOPE = 'enquiries.submissions';

/**
 * Locked: neither hidden nor moved. Institution identifies the row; Answers and
 * Actions are the only routes to a submission's detail and its actions, and a
 * route a preference can hide reads as a removed feature.
 */
const LOCKED_KEYS = [INSTITUTION_COLUMN, ANSWERS_KEY, ACTIONS_KEY] as const;
/** Rendered with `pinned` (`sticky right-0`) — not a second name for the locked set. */
const PINNED_KEYS = [ACTIONS_KEY] as const;
/*
 * ⚠️ Assigned is an ORDINARY column — first by default, but moved, hidden and
 * frozen through the column manager like any other. *SUPERSEDED 2026-10-02:
 * code-pinned LEFT via `pinnedLeftKeys`*, which fought user freezes and let a
 * scrolled column slide under two sticky ones.
 */

function lockReasonOf(key: string): string | undefined {
  if (key === INSTITUTION_COLUMN) return 'Identifies each submission';
  if (key === ANSWERS_KEY) return 'The only way to open a submission here';
  if (key === ACTIONS_KEY) return 'Every action on a submission lives in this column';
  return undefined;
}

/**
 * The pinned Actions column: the company name (truncating, with its `title`),
 * `Deal raised`, and one Actions menu. Added to the floor at the `<Table>` so
 * the pinned column never squeezes its neighbours. ⚠️ *SUPERSEDED 2026-10-01:
 * `180`, sized for a stacked Archive button.* One constant shared by all three
 * Enquiries sections now, so their Actions columns cannot drift apart.
 */
const ACTIONS_COLUMN_WIDTH = ENQUIRY_ACTIONS_COLUMN_WIDTH;

/**
 * The one filterable column.
 *
 * 🔴 **`options` IS DECLARED, NOT ACCUMULATED, AND THAT IS THE WHOLE POINT.**
 * Without it the hook builds the vocabulary out of the rows it has already
 * seen — so a region absent from the pages somebody has looked at is a region
 * they cannot filter by, and the popover gives no hint anything is missing. It
 * is worst on the rare value, which is exactly the one worth finding.
 * `form_region` is a CLOSED SET the contract declares (§3), so the complete
 * list can be stated here; it is a claim that the API accepts both values, and
 * an option its zod schema does not hold would come back a 422 and empty the
 * table.
 *
 * ⚠️ **`sortKey` IS PRESENT ONLY WHERE `EventInterestListParams['sort']`
 * DECLARES THE MEMBER**, and that union is deliberately narrow: a member
 * declared client-side and absent on the server grows a sortable-looking header
 * over a 422, which empties the table and reads as "nothing matches".
 * `FilterableTH` claims `aria-sort` only where a funnel carries a sort, so the
 * columns without one promise nothing.
 *
 * ✅ **`Answered` AND `Said yes` SORT ON THE SERVER — 2026-10-09** (backend
 * `32057ed` added `answered` and `saidYes` to the sort enum).
 * ⚠️ *SUPERSEDED 2026-10-09: "⛔ **`Answered` AND `Said yes` CARRY NO SORT.**
 * Both are counted from the row's own `events` array in the browser, so the
 * server cannot order by either — and a page-scoped sort that reordered one
 * page while implying the whole table was ordered is worse than no sort at
 * all."* The rule against a page-scoped sort stands; the server now orders
 * both. Their filter stays a page-scoped value list (owner's decision
 * 2026-10-02: a filter on every column), with no `range` because the server
 * has no params for one.
 */
const submissionColumns = (
  assigneeOptions: readonly ColumnFilterOption[] | null,
  rows: readonly SubmissionRow[],
): ColumnSpec<SubmissionRow>[] => [
  /**
   * Assigned — SERVER-scoped: one selection is sent as `assigneeId` (an id, or
   * `none` for the unassigned); several fall back to the loaded page. The
   * options are the Settings list (removed people included) plus Unassigned.
   */
  {
    ...assignmentColumnSpec<SubmissionRow>(COLUMNS[ASSIGNMENT_COLUMN].label, assigneeOptions, rows),
    sortKey: 'assignee',
  },
  /**
   * Institution — `company` orders by the institution's NAME. The value list
   * is page-scoped: it narrows the loaded rows only.
   * ⚠️ *SUPERSEDED 2026-10-02: "sort only" — owner wants a filter on every column.*
   */
  {
    key: INSTITUTION_COLUMN,
    label: COLUMNS[INSTITUTION_COLUMN].label,
    get: (row) => row.companyName,
    sortKey: 'company',
  },
  {
    key: CONTACT_COLUMN,
    label: COLUMNS[CONTACT_COLUMN].label,
    get: (row) => row.contactName,
    sortKey: 'contactName',
  },
  {
    key: REGION_COLUMN,
    label: COLUMNS[REGION_COLUMN].label,
    get: (row) => row.formRegion,
    /*
     * ⚠️ **`formRegionLabelOf`, NOT `formRegionLabel`, AND THE DIFFERENCE IS
     * DELIBERATE.** `ColumnSpec.format` is handed a bare `string` — the hook
     * cannot know a column's vocabulary — so this is the one site where the
     * value is genuinely untyped. Widening the narrow helper to accept
     * `string` to serve it would give up the compile check everywhere else.
     */
    format: (value) => formRegionLabelOf(value),
    sortKey: 'region',
    scope: 'server',
    param: 'formRegion',
    multi: true,
    /*
     * ⚠️ **THE VALUES ARE THE WIRE'S, not the reader's words**, and they go
     * into the query string verbatim — so the hyphen in `north-america` is
     * load-bearing: the underscore spelling is a 422 that empties the table,
     * which reads as *"no institution used that form"*.
     *
     * ⛔ **THE LIST IS IMPORTED RATHER THAN WRITTEN OUT HERE.**
     * `ColumnFilterOption.value` is a bare `string`, so a literal typed at this
     * call site is checked by nothing; `FORM_REGION_OPTIONS` is keyed to the
     * union, which is what turns a stale spelling into a compile error.
     */
    options: FORM_REGION_OPTIONS.map((option) => ({ ...option })),
  },
  {
    key: ANSWERED_COLUMN,
    label: COLUMNS[ANSWERED_COLUMN].label,
    get: (row) => row.answered,
    numeric: true,
    sortKey: 'answered',
  },
  {
    key: SAID_YES_COLUMN,
    label: COLUMNS[SAID_YES_COLUMN].label,
    get: (row) => row.saidYes,
    numeric: true,
    sortKey: 'saidYes',
  },
  /**
   * Submitted — server sort, page-scoped values.
   *
   * ⚠️ `submittedAt` is a real TIMESTAMP, so it is formatted with
   * `formatDateTime`; the plain `YYYY-MM-DD` bounds the API takes for the same
   * column are a different shape and no `Date` is constructed from either.
   */
  {
    key: SUBMITTED_COLUMN,
    label: COLUMNS[SUBMITTED_COLUMN].label,
    get: (row) => row.submittedAt,
    format: (value) => formatDateTime(value),
    sortKey: 'submittedAt',
  },
];

/* -------------------------------------------------------------------------- *
 * Cells
 * -------------------------------------------------------------------------- */

const APPROVAL_LABEL: Record<SubmissionRow['approvalStatus'], string> = {
  approved: 'Approved',
  pending: 'Awaiting approval',
  rejected: 'Rejected',
};

function CellContent({ columnKey, row }: { columnKey: string; row: SubmissionRow }) {
  switch (columnKey) {
    case ASSIGNMENT_COLUMN:
      return (
        <div className="min-w-0">
          <EnquiryAssignmentCell assignment={row.assignment} />
        </div>
      );
    case INSTITUTION_COLUMN:
      return (
        <div className="min-w-0">
          <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
            {row.companyName}
          </p>
          {/*
            ⚠️ **THE APPROVAL STATE IS ON THE ROW, not only in the demand
            columns.** A submission attaches before its company is approved, so
            a reader scanning this list has no other way to tell an institution
            somebody has vetted from one nobody has looked at.
            ⚠️ **Only where it is NOT approved**: a badge on every row is a
            badge nobody reads, and `approved` is the state that needs no
            marking.
          */}
          {row.approvalStatus !== 'approved' && (
            <p className="m-0 mt-0.5">
              <Badge tone={row.approvalStatus === 'pending' ? 'warning' : 'neutral'}>
                {APPROVAL_LABEL[row.approvalStatus]}
              </Badge>
            </p>
          )}
          {/* A real word on every archived row, in every view — a marker that
              depended on the pill pressed would mean two things in two views. */}
          {row.archived && (
            <p className="m-0 mt-0.5">
              <Badge tone="neutral">Archived</Badge>
            </p>
          )}
        </div>
      );
    case CONTACT_COLUMN:
      return (
        <div className="min-w-0">
          <p className="m-0 text-sm text-fg-secondary [overflow-wrap:anywhere]">
            {row.contactName}
          </p>
          <p className="m-0 mt-0.5 text-xs text-fg-muted [overflow-wrap:anywhere]">
            <EmailLink address={row.contactEmail} tone="muted" />
          </p>
        </div>
      );
    case REGION_COLUMN:
      return <span className="text-sm text-fg-secondary">{formRegionLabel(row.formRegion)}</span>;
    case ANSWERED_COLUMN:
      return <span className="tabular text-sm text-fg-muted">{row.answered}</span>;
    case SAID_YES_COLUMN:
      return <span className="tabular text-sm font-medium text-fg">{row.saidYes}</span>;
    case SUBMITTED_COLUMN:
      return (
        <span className="whitespace-nowrap tabular text-xs">{formatDateTime(row.submittedAt)}</span>
      );
    default:
      return null;
  }
}

/** Skeletons in the SHAPE of the real row, never a spinner. */
function SkeletonCellContent({ columnKey }: { columnKey: string }) {
  switch (columnKey) {
    case ASSIGNMENT_COLUMN:
    case INSTITUTION_COLUMN:
    case CONTACT_COLUMN:
      return (
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-2.5 w-24" />
        </div>
      );
    case ANSWERED_COLUMN:
    case SAID_YES_COLUMN:
      return <Skeleton className="ml-auto h-3 w-8" />;
    default:
      return <Skeleton className="h-3 w-24" />;
  }
}

/* -------------------------------------------------------------------------- *
 * The page
 * -------------------------------------------------------------------------- */

export function EventInterestPage() {
  const { ability } = useAuth();
  const canRead = ability.canRead(EVENT_INTEREST);
  /*
   * 🔴 **THE PROPOSALS SECTION ASKS ITS OWN MODULE** — see {@link B2B_PROPOSAL}
   * for why the two grants genuinely diverge and why borrowing the page's slug
   * is the `integrations` mistake.
   *
   * ⚠️ **AN ABSENT SCOPE RESOLVES ALLOWED FOR `read`**, so against a server
   * that has not catalogued the module this answers `true` and the section
   * behaves exactly as it did before this gate existed. **It degrades OPEN, and
   * that is the accepted direction here** — the alternative hides a section
   * from everybody the day a deploy runs out of order, on a page they can
   * already reach.
   */
  const canReadProposals = ability.canRead(B2B_PROPOSAL);
  /*
   * ⚠️ **`filter`, not a conditional push** — TABS stays the one place a tab's
   * label and value are spelled, so a pill cannot come to exist here that the
   * `Tab` union does not know about.
   */
  /* The report-download section asks its own module, like proposals. */
  const canReadReports = ability.canRead(REPORT_DOWNLOAD);
  /*
   * Layout manager (owner 2026-10-20): only the Demand tab has two movable
   * sections. Submissions, Proposals and Report leads are one panel each, so
   * they carry no manager. The defaults reproduce today's stack exactly.
   */
  const demandLayout = usePageLayout('enquiries.demand', [
    { id: 'festival-demand', label: 'Demand by festival' },
    { id: 'city-demand', label: 'Demand by city' },
  ]);
  const visibleTabs = useMemo(
    () =>
      TABS.filter(
        (item) =>
          (item.value !== 'proposals' || canReadProposals) &&
          (item.value !== 'reports' || canReadReports),
      ),
    [canReadProposals, canReadReports],
  );

  /*
   * The to-deal-with counts on the pills — UNASSIGNED enquiries only (owner,
   * 2026-10-16; the hook falls back to the open count on an older server), so
   * an assign or unassign must refresh them. ⚠️ **ONE instance, here**: the panels
   * report their successes upward through `onChanged` rather than calling the
   * hook themselves, so a refresh always lands on the counts that are drawn.
   */
  const { counts: enquiryCounts, refresh: refreshEnquiryCounts } = useEnquirySummary();

  /*
   * The Assigned funnel's vocabulary — EVERY person on the Settings list,
   * removed ones included (an enquiry can still be assigned to them), plus
   * Unassigned. Read once here and handed to all three tables. ⚠️ `null` while
   * unread or on a failed read: the tables then fall back to the names on the
   * loaded rows rather than offering a list that pretends to be complete.
   */
  const [assigneeOptions, setAssigneeOptions] = useState<readonly ColumnFilterOption[] | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    api
      .listEnquiryAssignees({}, controller.signal)
      .then(({ data }) => {
        if (!controller.signal.aborted) setAssigneeOptions(assigneeFilterOptions(data));
      })
      .catch(() => {
        /* Not a page failure: the funnel degrades to the loaded rows' names. */
      });
    return () => controller.abort();
  }, []);
  const tabCounts = useMemo(
    (): Partial<Record<Tab, number | null>> => ({
      submissions: enquiryCounts?.eventInterest ?? null,
      proposals: enquiryCounts?.b2bProposal ?? null,
      reports: enquiryCounts?.reportDownload ?? null,
    }),
    [enquiryCounts],
  );

  /*
   * ⚠️ `useSearchParams()` with NO argument. Its first parameter is a DEFAULT
   * INIT, not options — `useSearchParams({ replace: true })` injects a literal
   * `replace=true` into the query string and then into everything parsed out of
   * it. The `{ replace: true }` belongs on the setter.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  /*
   * The serialised query is every memo and effect dependency, never the parsed
   * object: a fresh object each render keys the fetch on identity and refetches
   * for ever. A string compares by value.
   */
  const queryString = searchParams.toString();

  /*
   * The default is spelled by the key's ABSENCE, and NOTHING is written on
   * arrival: `/event-interest` and `/event-interest?tab=demand` are one URL for
   * one screen rather than two for it, and an arrival write would race anything
   * else seeding the same query string off a snapshot taken before it.
   * Anything unrecognised reads as the default — an unknown pill is not a
   * reason to show nothing. `parseTab` is a positive test per member with a
   * fallback; see its own note for why it may never become a negation.
   */
  const tab: Tab = parseTab(searchParams.get(TAB_PARAM));

  /*
   * ⚠️ **`useId`, not a module constant.** The panel labels its own region with
   * this, and a literal would collide the day anything mounts this page twice —
   * which turns two regions into one accessible name rather than failing.
   */
  const proposalsHeadingId = useId();
  const reportsHeadingId = useId();

  const filters = useMemo(() => {
    const current = new URLSearchParams(queryString);
    return {
      projectIds: parseIdList(current.get('projectId')),
      companyId: parseId(current.get('companyId')),
      formRegions: parseRegionList(current.get('formRegion')),
      approvalStatuses: parseApprovalStatusList(current.get('approvalStatus')),
    };
  }, [queryString]);

  /**
   * One writer for every URL-backed filter.
   *
   * ⚠️ **`replace: true` on every write**: changing a filter is not a
   * navigation, and one history entry per press makes Back mean "undo one
   * filter".
   *
   * ⚠️ **An empty value DELETES the key rather than writing it empty** —
   * `?projectId=` is a 422 on a `.strict()` schema, not "no filter".
   */
  const setFilter = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(queryString);
      if (value === '') params.delete(key);
      else params.set(key, value);
      setSearchParams(params, { replace: true });
    },
    [queryString, setSearchParams],
  );

  const changeTab = useCallback(
    (next: Tab, extra?: Readonly<Record<string, string>>) => {
      /*
       * 🔴 **ONE `URLSearchParams`, EVERY WRITE, ONE `setSearchParams`.** A
       * second call would start from this render's snapshot, which the first
       * has not updated yet, so whichever lands last is the only one that
       * survives — and nothing errors. This is why the festival table hands
       * its `projectId` in here rather than writing it itself.
       */
      const params = new URLSearchParams(queryString);
      for (const key of CLEARED_ON_SWITCH) params.delete(key);
      if (next === 'demand') params.delete(TAB_PARAM);
      else params.set(TAB_PARAM, next);
      if (extra) for (const [key, value] of Object.entries(extra)) params.set(key, value);
      setSearchParams(params, { replace: true });
    },
    [queryString, setSearchParams],
  );

  /*
   * ⚠️ **DECLARED HERE, ABOVE `clearFilters`, RATHER THAN DOWN WITH THE REST OF
   * THE SUBMISSIONS STATE.** `clearFilters` closes over `setSearch`, and a
   * `useCallback` declared before the `useState` it reads is capturing a
   * binding that is still in its temporal dead zone — it happens to work
   * because the body runs later, which is exactly the kind of accident that
   * stops working when somebody calls it during render.
   */
  const [search, setSearch] = useRememberedState('submissions-search', '');

  const clearFilters = useCallback(() => {
    const params = new URLSearchParams(queryString);
    for (const key of CLEARED_ON_SWITCH) params.delete(key);
    setSearchParams(params, { replace: true });
    setSearch('');
  }, [queryString, setSearchParams, setSearch]);

  /* ── The demand report ────────────────────────────────────────────────────
   *
   * ⚠️ **FETCHED AT PAGE LEVEL, ON BOTH TABS, AND THAT IS A DELIBERATE
   * DEPARTURE FROM `/commissions`' "one section fetches" RULE.** That rule
   * exists because two mounted sections are two PAGERS writing one `page` key
   * and two paginated reads against a shared budget. This is a single
   * unpaginated aggregate with no pager and no key of its own, and it is the
   * MASTER the submissions tab reads its festival vocabulary from — a filter
   * offers what can MATCH, and a festival list built from the loaded page could
   * only ever offer the festivals already on screen.
   */
  const [demand, setDemand] = useState<EventInterestDemand | null>(null);
  const [demandError, setDemandError] = useState<string | null>(null);
  const [demandPending, setDemandPending] = useState(true);

  /**
   * ⚠️ **LIFTED OUT OF ITS EFFECT SO A DELETE CAN RE-RUN IT.** Deleting a
   * submission removes its answers and takes its company off the projects of
   * interest they named, so the DEMAND figures are wrong the moment it lands —
   * and this read fires once on mount. A reader who deletes on the submissions
   * tab and switches to Demand would otherwise read pre-delete figures, with no
   * error, no marker and a number that looks entirely plausible.
   *
   * ⛔ **A re-run does NOT put `demandPending` back to `true`.** The report is
   * already on screen and stays correct until the new response lands, so
   * flashing it into its loading state would take the festival picker away for
   * a moment on every delete — and `demandLoading` is what that picker reads.
   *
   * ⚠️ **The signal is optional because the refresh has nothing to abort
   * against**: the mount fetch passes one so leaving the page cancels it, and
   * the post-delete refresh is a one-shot. `.then` is unguarded in both cases
   * because an aborted fetch REJECTS rather than resolving.
   */
  const loadDemand = useCallback(
    (signal?: AbortSignal) =>
      api
        .eventInterestDemand(signal)
        // An `Envelope<T>`, as every single-record read here is — the list reads
        // answer `Paginated<T>` directly and the two are not interchangeable.
        .then(({ data }) => {
          setDemand(data);
          setDemandError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setDemandError(caught instanceof Error ? caught.message : 'Failed to load demand');
        })
        .finally(() => {
          if (!signal?.aborted) setDemandPending(false);
        }),
    [],
  );

  useEffect(() => {
    /*
     * ⛔ **NOTHING IS FETCHED FOR A REFUSED READER.** A request certain to be
     * answered `403` spends from a budget shared by the whole office, and the
     * refusal panel below says more than the failure would.
     *
     * ⚠️ **The refused branch sets no state**, which is why `demandLoading` is
     * DERIVED below rather than switched off here: a `setState` in this arm
     * would start a second render for a reader who is never going to see the
     * report at all.
     */
    if (!canRead) return;
    const controller = new AbortController();
    void loadDemand(controller.signal);
    return () => controller.abort();
  }, [canRead, loadDemand]);

  /**
   * ⚠️ **DERIVED, so the refused branch above sets no state.** A reader without
   * the grant never starts the fetch, so `demandPending` stays `true` for them
   * for ever — which would render skeletons under a refusal panel if this were
   * read raw. Deriving it costs nothing and removes a cascading render.
   */
  const demandLoading = canRead && demandPending;

  const cityRows = useMemo(
    () => (demand ? demand.cities.map(toDemandCityRow) : []),
    [demand],
  );
  const festivalRows = useMemo(
    () => (demand ? demand.festivals.map(toDemandFestivalRow) : []),
    [demand],
  );

  /* ── The submissions list ─────────────────────────────────────────────── */

  /* `search` is declared above, beside `clearFilters`, which closes over it. */
  const [result, setResult] = useState<Paginated<EventInterestSubmission> | null>(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  /** Opens on Live — the working queue. See {@link SubmissionView}. */
  const [submissionView, setSubmissionView] = useRememberedState<SubmissionView>(
    'submissions-view',
    LIVE_SUBMISSION_VIEW.value,
    (raw) => SUBMISSION_VIEWS.find((spec) => spec.value === raw)?.value,
  );
  const submissionViewSpec = submissionViewSpecOf(submissionView);

  const rows = useMemo(
    () => (result ? result.data.map(toSubmissionRow) : []),
    [result],
  );

  const columns = useMemo(() => submissionColumns(assigneeOptions, rows), [assigneeOptions, rows]);
  const table = useTableFilters(rows, columns);
  const { visibleRows, serverParams, sort, headerFor } = table;

  /* Column layout — order, hidden set and frozen edges, stored per account. */
  const layout = useColumnLayout(
    COLUMN_LAYOUT_SCOPE,
    LAYOUT_KEYS,
    LOCKED_KEYS,
    PINNED_KEYS,
  );
  const headRowRef = useRef<HTMLTableRowElement>(null);
  /* `layout.frozenSideOf` is stable; passing `layout` would re-subscribe the observer. */
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    layout.visibleKeys,
    layout.frozenSideOf,
    PINNED_KEYS,
  );
  /* Summed from the VISIBLE columns: a hidden column takes its width with it. */
  const tableMinWidth = useMemo(
    () =>
      layout.visibleKeys.reduce((total, key) => {
        if (isColumnKey(key)) return total + COLUMNS[key].width;
        if (key === ANSWERS_KEY) return total + ANSWERS_WIDTH;
        if (key === ACTIONS_KEY) return total + ACTIONS_COLUMN_WIDTH;
        return total;
      }, 0),
    [layout.visibleKeys],
  );
  const labelOf = useCallback((key: string): string => {
    if (isColumnKey(key)) return COLUMNS[key].label;
    if (key === ANSWERS_KEY) return 'Answers';
    if (key === ACTIONS_KEY) return 'Actions';
    return key;
  }, []);

  const serverSignature = JSON.stringify(serverParams);
  const sortSignature = sort ? `${sort.key}:${sort.direction}` : '';

  /**
   * 🔴 **RESET TO PAGE 1 ON EVERY SERVER-SIDE NARROWING.**
   *
   * Filtering from page 3 asks for page 3 of a much shorter result — usually
   * empty — which reads as *"no institution matches"* when the matches are all
   * on page 1. This repo shipped exactly that on `/users`, where the house rule
   * ("search inputs debounce at 250ms and reset to page 1") had simply never
   * been applied. **A rule written down once is not a rule applied everywhere.**
   *
   * ⚠️ `queryString` is in the dependency list, so switching tabs resets the
   * pager too — which is the other half of the same hazard.
   */
  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setPage(1);
  }, [search, queryString, serverSignature, sortSignature, submissionView]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      const params: EventInterestListParams = {
        page,
        perPage: PER_PAGE,
        ...submissionArchivedParamOf(submissionView),
      };
      // Every key is written only when it has a value: an empty string is a 422
      // on a `.strict()` schema rather than "no filter".
      if (search) params.search = search;

      const current = new URLSearchParams(queryString);
      const projectIds = parseIdList(current.get('projectId'));
      if (projectIds.length > 0) params.projectId = projectIds.join(',');
      const companyId = parseId(current.get('companyId'));
      if (companyId !== null) params.companyId = companyId;

      /*
       * ⚠️ **THE FUNNEL'S SELECTION IS TRANSFERRED BY LITERAL KEY, NOT BY
       * `Object.assign(params, serverParams)`.** That spread is how every other
       * list page does it and it is UNTYPED — a `param` this app invented would
       * be silently stripped or 422 with nothing in `tsc` to say so. Naming the
       * key here means a wrong one is a compile error.
       */
      const regions = parseRegionList(serverParams[REGION_COLUMN] ?? null);
      if (regions.length > 0) params.formRegion = regions.join(',');
      /* The Assigned funnel's selections: ids and/or `none` for unassigned, CSV. */
      const assigneeId = assigneeFilterOf(serverParams[ASSIGNEE_PARAM]);
      if (assigneeId !== null) params.assigneeId = assigneeId;

      /*
       * ⚠️ **THE APPROVAL FILTER IS THE LIST'S HALF OF THE UNAPPROVED SPLIT** —
       * the same separation the demand report makes with its own column, and
       * the reason the list can answer *"which institutions am I waiting on"*
       * rather than only showing a badge per row.
       */
      const approvals = parseApprovalStatusList(current.get('approvalStatus'));
      if (approvals.length > 0) params.approvalStatus = approvals.join(',');

      /*
       * ⚠️ **SENT ONLY WHEN A HEADER HAS ACTUALLY BEEN USED.** The endpoint has
       * its own default; asking for it by name is that ordering re-spelled as
       * an instruction, which pins this page to today's default for ever the
       * day the server's moves. **Nothing is how you ask for the default.**
       *
       * The cast is narrow and deliberate: `sort.key` is the column's own
       * `sortKey`, and every one of those is written against
       * `EventInterestListParams['sort']` — a union the type declares
       * explicitly so a rename is a `tsc` error here rather than a 422 at
       * runtime.
       */
      if (sort) {
        params.sort = sort.key as EventInterestListParams['sort'];
        params.order = sort.direction;
      }

      return api
        .listEventInterestSubmissions(params, signal)
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof Error ? caught.message : 'Failed to load event interest submissions');
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [page, search, queryString, sort, serverParams, submissionView],
  );

  useEffect(() => {
    if (!canRead || tab !== 'submissions') return;
    /*
     * Debounced at 250ms so typing does not fire a request per keystroke, and
     * the controller aborts the in-flight one when the term moves on.
     */
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load, canRead, tab]);

  /* ── One submission, in full ──────────────────────────────────────────── */

  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<SubmissionDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (openId === null) return;
    const controller = new AbortController();
    /*
     * 🔴 **THE PREVIOUS SUBMISSION IS CLEARED BEFORE THE NEW READ, AND THAT IS
     * A CORRECTNESS REQUIREMENT RATHER THAN HYGIENE.** The dialog's title comes
     * from the new row while its body comes from state, so without this a
     * reader opening a second institution sees the FIRST one's seven answers
     * under the second one's name — a confident wrong answer, for as long as
     * the fetch takes, on the screen whose whole job is "what did THIS
     * institution say".
     *
     * The lint rule is right in general and wrong here: this is an effect
     * synchronising with an external system, and the stale-state clear has to
     * happen when the subject changes rather than during render.
     */
    // oxlint-disable-next-line react/set-state-in-effect
    setDetailLoading(true);
    setDetail(null);
    setDetailError(null);
    api
      .getEventInterestSubmission(openId, controller.signal)
      .then(({ data }) => {
        setDetail(toSubmissionDetail(data));
        setDetailError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setDetailError(caught instanceof Error ? caught.message : 'Failed to load the submission');
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false);
      });
    return () => controller.abort();
  }, [openId]);

  /**
   * The projects this submission's `yes` answers named — what the delete takes
   * the company off, and what its confirmation has to state one by one.
   *
   * ⚠️ **BUILT FROM THE ANSWERS ALREADY ON SCREEN, never a second read.** The
   * dialog is opened over a record that is fully loaded, so there is nothing to
   * fetch; a per-row lookup here would be one request per submission against
   * the office's shared budget, for data the reader is already looking at.
   *
   * ⚠️ **`yes` ONLY.** A `no` answer is a real row and is rendered as one, but
   * it named no interest — so listing it in a confirmation would claim the
   * delete removes something it does not.
   *
   * ⚠️ **`answer.key` IS THE PROJECT'S ID** (its own doc says so), which is what
   * makes it a stable render key here rather than an index.
   */
  const removedProjects = useMemo(
    (): RemovedProject[] =>
      detail === null
        ? []
        : detail.festivals
            .filter((answer) => answer.response === 'yes')
            .map((answer) => ({ key: answer.key, name: answer.festivalName })),
    [detail],
  );

  /**
   * 🔴 **CLOSES THE DIALOG AND RE-READS BOTH TABS.**
   *
   * The dialog is showing a record that no longer exists, so leaving it open
   * would be a screen stating a submission's answers under its institution's
   * name after both have gone.
   *
   * ⚠️ **AND BOTH RELOADS ARE NEEDED, NOT JUST THE LIST.** The row is still in
   * `result` and the demand report still counts the answers — one is a list a
   * row longer than the truth, the other is a set of figures that are quietly
   * high on a tab the reader has not opened yet.
   *
   * ⚠️ **They are CALLED rather than triggered through a counter.** A nonce in a
   * dependency array that the effect body never reads is a hidden trigger, and
   * `exhaustive-deps` is right to call it one: the next person moving these
   * fetches around has nothing telling them what the extra dependency was for.
   *
   * ⚠️ **Neither call carries a signal, deliberately.** A refresh fired from an
   * event has nothing to abort against, and the reads are idempotent.
   */
  const handleSubmissionDeleted = useCallback(() => {
    setOpenId(null);
    void load();
    void loadDemand();
    refreshEnquiryCounts();
  }, [load, loadDemand, refreshEnquiryCounts]);

  /* ── Archiving an event interest submission ─────────────────────────────
   *
   * One click, both directions, one route (`api.setEventInterestSubmissionArchived`)
   * with the direction in the BODY. ⛔ Converting never archives — the two are
   * separate gestures (owner decision).
   *
   * ⚠️ **Gated on `canRead(EVENT_INTEREST)`**, the owner's ruling: archiving
   * is triage, and the people triaging are the people reading. The whole tab
   * already sits behind that test, so the gate is restated at the control only
   * so it survives the tab's gate moving.
   *
   * ⚠️ **Read back on the three-state rule the proposal archive uses**: a
   * `2xx` is not storage, so the response's own `archivedAt` is checked
   * against what was asked for; `undefined` (a server that does not report the
   * column) is not a contradiction and is not reported as one.
   */
  /*
   * 2026-10-02 (owner): archiving is `event-interest.triage`. *SUPERSEDED:
   * `canRead(EVENT_INTEREST)` — "the people triaging are the people reading".*
   * The two proposal and report tabs follow the same rule on their own modules.
   */
  const canArchiveSubmissions = ability.canTriage('event-interest');
  const canTriageProposals = ability.canTriage('b2b-proposal');
  const canTriageReports = ability.canTriage('report-download');
  const [archivingId, setArchivingId] = useState<number | null>(null);
  const [archiveError, setArchiveError] = useState<{ id: number; message: string } | null>(null);

  const toggleSubmissionArchived = useCallback(
    async (row: SubmissionRow) => {
      const next = !row.archived;
      setArchivingId(row.id);
      setArchiveError(null);
      try {
        const { data } = await api.setEventInterestSubmissionArchived(row.id, next);
        /* Widened to `unknown` so an older server's absent key is handled
           honestly even though the type declares the field. */
        const reported: unknown = data.archivedAt;
        if (reported !== undefined && (typeof reported === 'string') !== next) {
          throw new Error(
            next
              ? 'That submission still reads as live, so archiving it did not apply.'
              : 'That submission still reads as archived, so it was not brought back.',
          );
        }
        void load();
        refreshEnquiryCounts();
      } catch (caught: unknown) {
        setArchiveError({
          id: row.id,
          message: caught instanceof Error ? caught.message : 'That didn’t go through. Try again.',
        });
      } finally {
        setArchivingId(null);
      }
    },
    [load, refreshEnquiryCounts],
  );

  /* ── Creating a company for an enquiry ──────────────────────────────────
   *
   * 🔴 **ONE `CreateCompanyForEnquiry`, AT THE PAGE, SHARED BY BOTH PANELS** —
   * never one per row, for the reason the convert flows are mounted here.
   *
   * ⚠️ **BUSY-STATE CHOICE: THE PANEL'S PROMISE IS HELD OPEN UNTIL THE MODAL
   * CLOSES, AND ALWAYS RESOLVES.** The panels hold a row busy for the life of
   * the promise and re-read their list when it settles — the same contract the
   * convert flow relies on. Resolving immediately would re-read the list while
   * the modal was still open and then never again, so a newly linked company
   * would not show until somebody navigated away. Holding it open puts the
   * re-read at the one moment the row can have changed.
   *
   * ⛔ **IT NEVER REJECTS**, including on a cancel: the panel words a rejection
   * as *"That didn't go through"*, and a reader who closed the modal has had
   * nothing go wrong. The modal reports its own failures in place. And the
   * settle ref is cleared before it is called, so it cannot be spent twice —
   * an unsettled promise would leave every row in the panel disabled for ever.
   */
  const [creatingCompany, setCreatingCompany] = useState<CreatingCompany | null>(null);
  const settleCreateCompany = useRef<(() => void) | null>(null);

  const closeCreateCompany = useCallback(() => {
    setCreatingCompany(null);
    const settle = settleCreateCompany.current;
    settleCreateCompany.current = null;
    settle?.();
  }, []);

  /*
   * ⚠️ **`countryId` IS `null` FOR A PROPOSAL, DELIBERATELY.** The enquiry
   * carries `institutionCountry` as a NAME (and an ISO code), never a master
   * row id, and matching a typed name against `/countries` would be free text
   * read as an identity. The person chooses the country in the form.
   */
  const createCompanyForProposal = useCallback<ProposalRowAction>(
    (row) =>
      new Promise<void>((resolve) => {
        settleCreateCompany.current?.();
        settleCreateCompany.current = resolve;
        setCreatingCompany({
          kind: 'proposal',
          id: row.id,
          seed: { name: row.institutionName, countryId: null },
        });
      }),
    [],
  );

  /* A report lead carries no country at all — only the organisation as typed. */
  const createCompanyForReport = useCallback<ReportDownloadRowAction>(
    (row) =>
      new Promise<void>((resolve) => {
        settleCreateCompany.current?.();
        settleCreateCompany.current = resolve;
        setCreatingCompany({
          kind: 'report',
          id: row.id,
          seed: { name: row.organisation, countryId: null },
        });
      }),
    [],
  );

  /*
   * The link write. ⛔ **It throws on failure** so the modal can say the
   * company exists and the enquiry does not know about it — swallowing would
   * report a link that did not land.
   */
  const linkCreatedCompany = useCallback(
    async (companyId: number) => {
      const target = creatingCompany;
      if (target === null) return;
      if (target.kind === 'proposal') {
        await api.setB2bProposalSubmissionCompany(target.id, companyId);
      } else {
        await api.setReportDownloadLeadCompany(target.id, companyId);
      }
    },
    [creatingCompany],
  );

  /* ── Creating a contact for an enquiry (owner, 2026-10-02) ──────────────
   *
   * The same shape as the company block above, on all three tabs: ONE
   * `CreateContactForEnquiry` at the page, the panel's promise held open
   * until the flow closes, and ⛔ never rejected — a cancel is not a failure.
   * The settle ref is cleared before it is called, so it settles once.
   */
  const [creatingContact, setCreatingContact] = useState<CreatingContact | null>(null);
  const settleCreateContact = useRef<(() => void) | null>(null);

  const closeCreateContact = useCallback(() => {
    setCreatingContact(null);
    const settle = settleCreateContact.current;
    settleCreateContact.current = null;
    settle?.();
  }, []);

  const openCreateContact = useCallback(
    (target: CreatingContact) =>
      new Promise<void>((resolve) => {
        settleCreateContact.current?.();
        settleCreateContact.current = resolve;
        setCreatingContact(target);
      }),
    [],
  );

  /* ⚠️ `countryId: null` on both seeds, for the reason given at the company flow. */
  const createContactForProposal = useCallback<ProposalRowAction>(
    (row) =>
      openCreateContact({
        kind: 'proposal',
        id: row.id,
        request: {
          company:
            row.companyId !== null
              ? { id: row.companyId, name: row.companyName ?? `Company #${row.companyId}` }
              : null,
          companySeed: { name: row.institutionName, countryId: null },
          prefill: {
            ...contactNamePrefill(row.contactName),
            email: row.contactEmail,
            phone: row.contactPhone,
            title: row.contactDesignation,
          },
        },
      }),
    [openCreateContact],
  );

  /*
   * The lead's own halves (`row.firstName` / `row.lastName`) are passed as
   * typed, so nothing is re-split; `personName` seeds the full name only.
   */
  const createContactForReport = useCallback<ReportDownloadRowAction>(
    (row) =>
      openCreateContact({
        kind: 'report',
        id: row.id,
        request: {
          company: row.company,
          companySeed: { name: row.organisation, countryId: null },
          prefill: {
            name: row.personName,
            firstName: row.firstName,
            lastName: row.lastName,
            email: row.email,
            phone: row.phone,
            title: row.designation,
          },
        },
      }),
    [openCreateContact],
  );

  /* A submission is attached to its company at ingest, so it is always locked. */
  const createContactForSubmission = useCallback(
    (row: SubmissionRow) =>
      openCreateContact({
        kind: 'submission',
        id: row.id,
        request: {
          company: { id: row.companyId, name: row.companyName },
          companySeed: { name: row.companyName, countryId: null },
          prefill: {
            ...contactNamePrefill(row.contactName),
            email: row.contactEmail,
            phone: row.contactPhone,
            title: row.contactDesignation,
          },
        },
      }),
    [openCreateContact],
  );

  /* The link write for an enquiry with no company. ⛔ Throws on failure, as above. */
  const linkContactCompany = useCallback(
    async (companyId: number) => {
      const target = creatingContact;
      if (target === null || target.kind === 'submission') return;
      if (target.kind === 'proposal') {
        await api.setB2bProposalSubmissionCompany(target.id, companyId);
      } else {
        await api.setReportDownloadLeadCompany(target.id, companyId);
      }
    },
    [creatingContact],
  );

  /* ── Assigning an enquiry ───────────────────────────────────────────────
   *
   * ONE `AssignEnquiryModal`, at the page, shared by all three tabs — the
   * company and contact flows' shape: the panel's promise is held open until
   * the modal closes and ⛔ never rejects (a cancel is not a failure); the
   * panel re-reads its list and calls `onChanged` when it settles. The
   * submissions tab reloads directly in `onDone`.
   *
   * Offered only to a reader holding `<source>.triage` — the panels receive
   * `onAssign` only inside the page's triage block, and the submissions menu
   * tests `canArchiveSubmissions`, which is `event-interest.triage`.
   */
  const [assigning, setAssigning] = useState<AssigningEnquiry | null>(null);
  const settleAssign = useRef<(() => void) | null>(null);

  const closeAssign = useCallback(() => {
    setAssigning(null);
    const settle = settleAssign.current;
    settleAssign.current = null;
    settle?.();
  }, []);

  const openAssign = useCallback(
    (target: AssigningEnquiry) =>
      new Promise<void>((resolve) => {
        settleAssign.current?.();
        settleAssign.current = resolve;
        setAssigning(target);
      }),
    [],
  );

  const assignProposal = useCallback<ProposalRowAction>(
    (row) =>
      openAssign({
        source: 'b2b-proposal',
        id: row.id,
        name: row.institutionName,
        current: row.assignment ?? null,
      }),
    [openAssign],
  );

  const assignReport = useCallback<ReportDownloadRowAction>(
    (row) =>
      openAssign({
        source: 'report-download',
        id: row.id,
        name: row.organisation,
        current: row.assignment ?? null,
      }),
    [openAssign],
  );

  const assignSubmission = useCallback(
    (row: SubmissionRow) =>
      openAssign({
        source: 'event-interest',
        id: row.id,
        name: row.companyName,
        current: row.assignment ?? null,
      }),
    [openAssign],
  );

  /* ══ THE PROPOSAL ENQUIRIES' FOUR WRITES ═══════════════════════════════════
   *
   * 🔴 **THE THREE TIERS ARE DIFFERENT AND NOTHING HERE HARMONISES THEM**, and
   * `api.ts` says so in terms at the three methods — *"the gradient is the
   * consequence of the gesture rather than the shape of the verb."*
   *
   * | control   | the server's guard                    |
   * | --------- | ------------------------------------- |
   * | archive   | `denyApiKey` and **nothing else**     |
   * | the deal  | `denyApiKey` + `WRITE_ROLES`          |
   * | delete    | `denyApiKey` + `ADMIN_ROLES`          |
   *
   * ⛔ **AND NOT ONE OF THE FOUR IS GATED HERE, WHICH IS NOT THE SAME AS BEING
   * UNGATED.** `ProposalsPanel` applies all three tests itself — `ability.isAdmin`
   * for the delete, `ability.canCreate('deals')` for the convert, and no test at
   * all for archive and unarchive — and its own header states the rule this page
   * is obeying: **every gate is in the panel rather than at its call sites, so
   * the table and the phone card cannot disagree about who may destroy a
   * record.** A second spelling here is how a destructive control comes to appear
   * on one surface and not the other, so these four are passed unconditionally
   * and the panel decides which are drawn.
   *
   * ⚠️ **NONE OF THEM MAY BE GATED ON `canRead(EVENT_INTEREST)` EITHER.** A
   * proposal is read with `b2b-proposal.read`, a different grant, and this page's
   * own module has no authority over it — see the mount below.
   *
   * ⛔ **EVERY ONE OF THE FOUR REJECTS ON FAILURE AND NONE SWALLOWS.** The panel
   * reads a resolved promise as *the work has landed* — it clears the row's busy
   * state and re-reads the list on it — so a swallowed failure is a screen
   * reporting a write that did not happen. The one deliberate exception is the
   * delete's `404`, named at that handler.
   *
   * ⚠️ **AND NONE OF THEM COMPOSES A REFUSAL.** An `ApiError` is allowed through
   * untouched so the panel renders the server's own sentence; ours would sit in
   * front of it. The two sentences this page could not write are the ones that
   * matter most — see {@link recordDeal}.
   */

  /**
   * Archive an enquiry, or bring it back — **one route, one boolean, both
   * directions.**
   *
   * ⛔ **THE DIRECTION IS IN THE BODY AND NEVER INFERRED FROM THE PATH**, because
   * there is only one path: `PATCH /submissions/:id/archive`. There is no
   * `/unarchive`, so a hardcoded `{ archived: true }` behind a control labelled
   * *Unarchive* is a `200` that archives. The two callers below pass the
   * direction they mean.
   *
   * 🔴 **THE READ KEY AND THE WRITE KEY ARE DIFFERENT FACTS, SO A `2xx` CONFIRMS
   * NOTHING ON ITS OWN.** This writes `archived`, a **boolean**; the record
   * reports `archivedAt`, a **stamp**. A `2xx` is not storage — proved three
   * times on Xero and once on `/users` — and *"an un-archive that silently did
   * nothing looks exactly like a list that has not refreshed."* So the response's
   * own `archivedAt` is read back and checked against what was asked for.
   *
   * ⚠️ **THREE STATES, AND ONLY ONE OF THEM IS A CONTRADICTION.** `archivedAt` is
   * OPTIONAL because this app can deploy ahead of the API:
   *
   * | reads back | means | what this does |
   * | --- | --- | --- |
   * | `undefined` | **this server does not report the column** | nothing to check against — resolve, because a failure we cannot demonstrate must not be reported as one |
   * | a `string` | archived, at that moment | agrees with `archived: true` |
   * | `null` | live, nobody archived it | agrees with `archived: false` |
   *
   * ⛔ **`typeof reported === 'string'`, NEVER `reported !== null`.** The absent
   * key reads `undefined`, `undefined !== null` is **true**, and every enquiry on
   * an older server would read as archived — a measured production defect one
   * interface up, reproduced key for key.
   *
   * ⚠️ **The refusal is a plain `Error`, so the panel words it generically**
   * (*"That didn't go through. Try again."*), and here that sentence is both true
   * and the right next action: the write provably did not apply. ⛔ **It is
   * deliberately NOT dressed as an `ApiError`** — `instanceof ApiError` means *the
   * server said this* everywhere in this app, and manufacturing one would make a
   * client-side judgement indistinguishable from a refusal that arrived on the
   * wire.
   */
  const setProposalArchived = useCallback(async (id: number, archived: boolean) => {
    const { data } = await api.setB2bProposalSubmissionArchived(id, archived);
    const reported = data.archivedAt;
    // The server does not report this column: there is nothing to verify the
    // write against, and inventing a verdict from its silence would be a claim.
    if (reported === undefined) return;
    const isArchived = typeof reported === 'string';
    if (isArchived !== archived) {
      throw new Error(
        archived
          ? 'That enquiry still reads as live, so archiving it did not apply.'
          : 'That enquiry still reads as archived, so it was not brought back.',
      );
    }
  }, []);

  const archiveProposal = useCallback<ProposalRowAction>(
    (row) => setProposalArchived(row.id, true),
    [setProposalArchived],
  );

  const unarchiveProposal = useCallback<ProposalRowAction>(
    (row) => setProposalArchived(row.id, false),
    [setProposalArchived],
  );

  /**
   * Destroy one enquiry. **`204`, no body, and nothing to read back.**
   *
   * ⛔ **THE ABSENCE OF A REPORT IS NOT A REPORT OF ABSENCE**, which is why the
   * panel's confirmation names what is lost BEFORE the call, from what it already
   * holds, rather than reporting it afterwards. Nothing is added here.
   *
   * ⚠️ **A `404` IS TREATED AS DONE, AND IT IS THE ONE PLACE ON THIS PAGE A
   * FAILURE IS SWALLOWED.** `api.ts` names it as the ordinary race — somebody
   * else got there first, **the record is gone either way**, and whatever is on
   * screen is stale. Rejecting would print a failure over a record that really
   * has gone and leave the confirmation open on it; resolving closes the dialog
   * and re-reads the list, which is the state that is true. ⛔ **It is matched on
   * the status alone and widens to nothing else** — a `403` and a `409` are real
   * refusals and are rethrown so the panel renders the server's sentence.
   */
  const deleteProposal = useCallback<ProposalRowAction>(
    (row) =>
      api.deleteB2bProposalSubmission(row.id).catch((caught: unknown) => {
        if (caught instanceof ApiError && caught.status === 404) return;
        throw caught;
      }),
    [],
  );

  /* ── Turning an enquiry into a deal ──────────────────────────────────────
   *
   * 🔴 **THE FLOW IS MOUNTED ONCE, AT THE PAGE, AND NEVER PER ROW.** It mounts
   * `CompanyFormModal` and `DealFormModal`, each a large component with its own
   * hooks and its own rate-limited reads, so one per row on a list of twenty is
   * twenty. `CompanyProjectLinesFlow` and `CompanyProjectsModal` are mounted the
   * same way for the same reason — and the second half of that ruling matters
   * here too: **one record is legitimately on screen more than once**, and a
   * modal that refreshed only the row it was opened from would leave every
   * sibling stating the old thing.
   */

  /**
   * The enquiry the convert flow is running over, or `null` when it is closed.
   *
   * 🔴 **IT HOLDS THE WHOLE `B2bProposalSubmission`, FETCHED, AND IS NEVER BUILT
   * FROM THE ROW.** `ProposalRow` is a VIEW MODEL: its `services[].key` is
   * `${slug}:${index}` — a render key, not the sender's slug, which lives on
   * `sourceKey` — so a `B2bProposalSubmission` assembled from one would hand the
   * flow keys that resolve against no mapping, and **every service would be
   * reported as unmapped on a deal that should have carried lines.** The flow
   * requires the wire type; the wire type is what it is given.
   */
  const [converting, setConverting] = useState<B2bProposalSubmission | null>(null);

  /**
   * The `resolve` of the promise {@link convertProposal} handed the panel.
   *
   * ⚠️ **A REF RATHER THAN STATE: NOTHING RENDERS IT**, and making it state would
   * add a re-render for a fact nothing on screen shows.
   *
   * 🔴 **IT MUST ALWAYS BE SETTLED OR THE ROW STAYS BUSY FOR EVER.** The panel
   * holds `busyId` for the life of the promise and disables every other row's
   * controls with it, so a promise that never settles is a list nobody can act on
   * again — with no error, and looking exactly like a hang.
   */
  const settleConvert = useRef<(() => void) | null>(null);

  /**
   * The flow has finished, whatever it did.
   *
   * ⚠️ **IT RESOLVES ON EVERY EXIT, INCLUDING A CANCEL, AND NEVER REJECTS.** The
   * panel words a rejection as *"That didn't go through"*, and somebody who opened
   * the chooser and changed their mind has had nothing go wrong. **The flow
   * reports its own outcome, step by step, in its own report modal** — a company
   * created without a deal, a deal created without a link — and a second verdict
   * of ours beside it would be the one that is wrong.
   *
   * ⚠️ **The ref is cleared BEFORE it is called**, so a re-entrant close cannot
   * reach a settle that has already been spent.
   */
  const closeConvert = useCallback(() => {
    setConverting(null);
    const settle = settleConvert.current;
    settleConvert.current = null;
    settle?.();
  }, []);

  /**
   * Open the convert flow over one enquiry, and **stay unresolved until it
   * closes.**
   *
   * 🔴 **THE PROMISE IS THE REFRESH.** `ProposalRowAction` is documented as
   * resolving *when the work has landed*, and the panel re-reads its own list the
   * moment it does. Resolving immediately — the obvious shape for a control that
   * only opens a modal — would re-read the list while the flow was still on its
   * first step, and then never again: the enquiry would gain a deal with **no
   * reload afterwards**, so the row would go on offering *Convert to a deal*
   * until somebody navigated away. Holding the promise open puts the reload at
   * the one moment the row can have changed.
   *
   * ⚠️ **The row is busy, and every other row is disabled, for as long as the
   * flow is open.** That is the panel's `anyBusy` doing exactly what it says — a
   * second write would race the re-read — and it is behind a modal throughout.
   *
   * 🔴 **THE RECORD IS RE-READ BEFORE THE FLOW OPENS, AND `proposalIsConvertible`
   * IS ASKED OF WHAT CAME BACK.** The panel's own convertible test reads
   * `row.dealId`, which `linkedDealOf` answers `null` for every row until a field
   * lands — so today it offers the control on everything, and the fetched record
   * is the only place the truth lives. ⛔ **It reads the PAIR** (`dealId` and
   * `convertedAt`) rather than the id alone, because `null` covers *never
   * converted* AND *converted, and the deal has since been deleted* — and offering
   * the control on the second raises **a second deal for an enquiry meant to have
   * exactly one**, in good faith, with a `201` and nothing to find afterwards.
   *
   * ⚠️ **THE COST OF REFUSING HERE, STATED: the panel words a plain `Error`
   * generically, so this refusal reads *"That didn't go through. Try again."* —
   * and trying again will do the same.** It is the smaller of two wrongs: the
   * alternative is letting the flow run to the end and raising a real deal whose
   * link is then refused, which is the outcome `proposalIsConvertible` exists to
   * prevent. ⛔ **Do not fix the sentence by manufacturing an `ApiError`**; see
   * {@link setProposalArchived}. ⚠️ **The branch is unreachable while the server
   * sends neither field**, and closes on its own when it does: the panel's
   * `linkedDealOf` becomes real in the same breath and withholds the button, at
   * which point this is a race guard rather than the only guard.
   *
   * ⚠️ **No `AbortSignal`, deliberately** — a read fired from a press has nothing
   * to abort against, and aborting would reject the promise the panel is holding,
   * reporting a failure to somebody who merely moved on.
   */
  const convertProposal = useCallback<ProposalRowAction>(
    (row) =>
      new Promise<void>((resolve, reject) => {
        api
          .getB2bProposalSubmission(row.id)
          .then(({ data }) => {
            if (!proposalIsConvertible(data)) {
              reject(new Error('That enquiry already has a deal, so it cannot be converted again.'));
              return;
            }
            settleConvert.current = resolve;
            setConverting(data);
          })
          /* A failed read is an `ApiError`, so the panel renders the server's own
             sentence — a `403` on this module is an ordinary answer for most of
             the company and must not be worded as a fault by us. */
          .catch(reject);
      }),
    [],
  );

  /**
   * Record the deal the flow just created — **the write that blocks a second
   * conversion.**
   *
   * 🔴 **THE CONVERT AND THE RECORD ARE TWO VERBS OVER ONE COLUMN, AND THIS PAGE
   * HAS EXACTLY ONE WRITER OF IT.** `api.ts` warns at this method that a convert
   * CREATES a deal where this RECORDS one that already exists, and that *"if both
   * ever write the same column, two verbs own one field and the second to arrive
   * wins with a `200`."* ⛔ **That is not what is happening here, and the
   * difference is structural rather than a matter of care:** `ProposalConvertFlow`
   * writes the column through nothing of its own — it has no `api` call for it at
   * all and takes the write as a REQUIRED prop — so this function, reached only
   * as that prop, is the single path by which a converted enquiry's `dealId` is
   * ever set from this page. ⛔ **Nothing else here may write it**, and in
   * particular no *"record an existing deal"* control may be added beside the
   * convert without the two being reconciled first: that would be the second
   * independent writer the warning is about.
   *
   * 🔴 **IT REJECTS ON FAILURE, AND THAT IS THE WHOLE POINT OF THE PROP.** The
   * flow reads a resolved promise as *the link landed*. A swallowed failure would
   * leave a real deal raised and unrecorded while the enquiry went on reporting
   * itself unconverted — so it could be converted again tomorrow, which is the
   * state this write exists to make impossible.
   *
   * ## ⛔ TWO REFUSALS, TWO NEXT ACTIONS, AND NEITHER IS WORDED HERE
   *
   * | code | status | what it means | where the reader goes |
   * | --- | --- | --- | --- |
   * | `B2B_PROPOSAL_DEAL_ALREADY_LINKED` | **409** | this enquiry already names a deal | clear the link first |
   * | `B2B_PROPOSAL_DEAL_UNKNOWN` | **422** | the deal named does not exist | name a different deal |
   *
   * ⛔ **NEITHER IS CAUGHT, CLASSIFIED OR REWRITTEN.** The `ApiError` is allowed
   * through untouched, so the flow's report renders `caught.message` — **the
   * server's own sentence, which is where the difference between the two lives.**
   * A branch here would put our wording in front of theirs and would have to keep
   * two next actions in step with a module in another repository. ⚠️ **Note the
   * 409 fires on re-linking the SAME deal too** — the guard asks whether a deal is
   * set, not which — so a retry after a lost response is a refusal rather than a
   * no-op, and the flow's report says plainly that the deal exists and the enquiry
   * does not know about it.
   *
   * ⚠️ **THE LINK IS READ BACK, ON THE SAME THREE-STATE RULE AS THE ARCHIVE.**
   * `dealId` may read `undefined` — *not reported*, nothing to check, and a
   * verdict invented from silence is a claim. An explicit `null`, or somebody
   * else's id, is a contradiction and is refused.
   *
   * ⚠️ *SUPERSEDED 2026-09-20, the JUSTIFICATION and not the guard: "`api.ts`
   * says the column is absent from migrations 228–231, so `dealId` may read
   * `undefined`."* **Measured here rather than paraphrased from either comment:**
   * `migrations/232_add_b2b_proposal_triage.sql` adds `deal_id`, `converted_at`
   * and `archived_at` — and it is **UNTRACKED, in no commit at all**, as is the
   * `PATCH /submissions/:id/deal` route itself, which is absent from the
   * backend's committed `HEAD`. ⛔ **So the column is COMING and is NOT THERE
   * TODAY**, and neither is the endpoint: against the deployed server this call
   * is a `404`, which throws and is reported as the failure it is.
   *
   * ⛔ **THE GUARD STAYS WHATEVER ITS JUSTIFICATION DOES.** A read-back cannot
   * verify a field the server did not send, whichever migration is to blame, so
   * `undefined` goes on meaning *not reported*. **The early return is not a shim
   * waiting for 232 to land**, and deleting it once the column exists would turn
   * an older or partially-deployed API into a false contradiction.
   *
   * ✅ **CLOSED 2026-09-20 — IT RETURNS THE OUTCOME INSTEAD OF RESOLVING
   * SILENTLY.** ⚠️ *SUPERSEDED: "🔴 **THE OPEN HALF, REPORTED AND NOT FIXED
   * HERE: A RESOLVED PROMISE IS READ BY THE FLOW AS *the link landed*.** So
   * against a server that answers `200` and echoes no `dealId`, this returns
   * early, resolves, and `ProposalConvertFlow` tells the reader the deal was
   * recorded on an enquiry that does not know about it … The honest sentence
   * belongs in `ProposalConvertFlow.tsx`, which this page does not own."*
   *
   * ⛔ **`'unconfirmed'` IS NOT A FAILURE AND MUST NOT BE THROWN.** Throwing
   * would make the flow say *the deal was not recorded*, which is a verdict
   * nothing here can support: a `200` with no echo is equally consistent with
   * the write having landed. The two states send a reader to opposite places —
   * a refused write leaves the enquiry safe to convert again, an unconfirmed one
   * may already be linked — so the third state is returned rather than folded
   * into either neighbour. See {@link ProposalDealLinkOutcome}.
   */
  const recordDeal = useCallback(
    async (submissionId: number, dealId: number): Promise<ProposalDealLinkOutcome> => {
      const { data } = await api.setB2bProposalSubmissionDeal(submissionId, dealId);
      const recorded = data.dealId;
      if (recorded === undefined) return 'unconfirmed';
      if (recorded !== dealId) {
        throw new Error(
          recorded === null
            ? 'The enquiry still reads as unlinked, so the deal was not recorded on it.'
            : `The enquiry reads as linked to deal #${recorded} rather than #${dealId}.`,
        );
      }
      return 'recorded';
    },
    [],
  );

  /* ── Report downloads: the same four handlers, over the lead routes ───────
   *
   * ⚠️ **Each mirrors its proposal twin above line for line**, and each carries
   * that twin's reasoning: the archive reads `archivedAt` back on the
   * three-state rule; the delete swallows a `404` and nothing else; the convert
   * re-reads the record and asks the shared gate before the flow opens, and
   * holds the panel's promise until the flow closes; the deal link is read back
   * and returns `'unconfirmed'` rather than inventing a verdict from silence.
   * ⛔ None composes a refusal — the server's sentence reaches the panel.
   */

  const setReportArchived = useCallback(async (id: number, archived: boolean) => {
    const { data } = await api.setReportDownloadLeadArchived(id, archived);
    const reported = data.archivedAt;
    if (reported === undefined) return;
    const isArchived = typeof reported === 'string';
    if (isArchived !== archived) {
      throw new Error(
        archived
          ? 'That lead still reads as live, so archiving it did not apply.'
          : 'That lead still reads as archived, so it was not brought back.',
      );
    }
  }, []);

  const archiveReport = useCallback<ReportDownloadRowAction>(
    (row) => setReportArchived(row.id, true),
    [setReportArchived],
  );

  const unarchiveReport = useCallback<ReportDownloadRowAction>(
    (row) => setReportArchived(row.id, false),
    [setReportArchived],
  );

  const deleteReport = useCallback<ReportDownloadRowAction>(
    (row) =>
      api.deleteReportDownloadLead(row.id).catch((caught: unknown) => {
        if (caught instanceof ApiError && caught.status === 404) return;
        throw caught;
      }),
    [],
  );

  /** The whole lead, FETCHED — never assembled from the view-model row. */
  const [convertingReport, setConvertingReport] = useState<ReportDownloadLead | null>(null);
  /** Must always be settled, or the row stays busy for ever. */
  const settleReportConvert = useRef<(() => void) | null>(null);

  const closeReportConvert = useCallback(() => {
    setConvertingReport(null);
    const settle = settleReportConvert.current;
    settleReportConvert.current = null;
    settle?.();
  }, []);

  const convertReport = useCallback<ReportDownloadRowAction>(
    (row) =>
      new Promise<void>((resolve, reject) => {
        api
          .getReportDownloadLead(row.id)
          .then(({ data }) => {
            if (!reportLeadIsConvertible(data)) {
              reject(new Error('That lead already has a deal, so it cannot be converted again.'));
              return;
            }
            settleReportConvert.current = resolve;
            setConvertingReport(data);
          })
          .catch(reject);
      }),
    [],
  );

  const recordReportDeal = useCallback(
    async (leadId: number, dealId: number): Promise<ProposalDealLinkOutcome> => {
      const { data } = await api.setReportDownloadLeadDeal(leadId, dealId);
      const recorded = data.dealId;
      if (recorded === undefined) return 'unconfirmed';
      if (recorded !== dealId) {
        throw new Error(
          recorded === null
            ? 'The lead still reads as unlinked, so the deal was not recorded on it.'
            : `The lead reads as linked to deal #${recorded} rather than #${dealId}.`,
        );
      }
      return 'recorded';
    },
    [],
  );

  /* ── The festival picker's options ────────────────────────────────────── */

  /**
   * 🔴 **THE OPTIONS COME FROM THE DEMAND REPORT, WHICH IS THE MASTER FOR THIS
   * FILTER.**
   *
   * ⚠️ **NOT from the loaded page**, which could only ever offer the festivals
   * already on screen — and it is missing exactly the one somebody opened the
   * filter to find. ⚠️ **And not from `/projects` either**: 118 active projects
   * against seven festivals would offer 111 options that can only ever answer
   * zero rows, which is the failure the `hasCompanies` narrowing on the country
   * funnel was built to end. **A filter offers what can MATCH.**
   *
   * ⚠️ **It reads the FESTIVAL rows, never the city rows.** Three of the seven
   * festivals tour one place and store no cities at all, so a list built from
   * the city table would be silently missing those three.
   *
   * 🔴 **A HELD VALUE ABSENT FROM THE LIST IS PINNED BACK.** A `Combobox`
   * renders its PLACEHOLDER for a value it cannot find, so a link naming a
   * festival the report does not list would read as *"All festivals"* over a
   * narrowed table — a confident wrong label where the old `Select` showed a
   * visible blank. This repo shipped that regression twice on the revenue
   * panels' `regionKey`.
   */
  const festivalOptions = useMemo((): ComboboxOption[] => {
    const options = festivalRows.map((row) => ({
      value: String(row.festivalId),
      label: row.festivalName,
    }));
    const missing = filters.projectIds.filter(
      (held) => !options.some((option) => option.value === String(held)),
    );
    if (missing.length === 0) return options;
    return [
      ...options,
      ...missing.map((held) => ({
        value: String(held),
        label: `Festival #${held}`,
        hint: 'Not in the demand report',
      })),
    ];
  }, [festivalRows, filters.projectIds]);

  /* ── Derived view state ───────────────────────────────────────────────── */

  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastRow = Math.min(page * PER_PAGE, total);
  /* The first paint only — a refetch under an existing result dims the rows
     rather than replacing them with skeletons, so the page does not flash. */
  const initialLoad = loading && result === null;

  const activeFilterCount =
    (filters.projectIds.length > 0 ? 1 : 0) +
    (filters.companyId !== null ? 1 : 0) +
    (filters.formRegions.length > 0 ? 1 : 0) +
    (filters.approvalStatuses.length > 0 ? 1 : 0);
  const anyFilter = activeFilterCount > 0 || search !== '' || table.activeCount > 0;

  const emptyState = (
    <SubmissionsEmptyState
      filtered={anyFilter}
      hasError={error !== null}
      onClear={clearFilters}
      view={submissionViewSpec}
    />
  );

  /**
   * The stacked Actions slots for one row — rendered by BOTH the table's pinned
   * column and the sub-`md` card, from this one function, so the two layouts
   * cannot disagree about what a submission can do.
   */
  const submissionActions = (row: SubmissionRow) => (
    /*
     * The shared Enquiries actions cell (owner, 2026-10-01): the company — always
     * present on this tab, every submission is attached at ingest — and the deal
     * above one Actions menu. ⚠️ There is NO client-side convert for an event
     * interest submission (the server raises the deal at ingest), so a row with
     * no deal states nothing rather than offering an invented control.
     */
    <EnquiryRowActions
      rowName={row.companyName}
      company={{ id: row.companyId, name: row.companyName }}
      deal={row.dealId !== null ? { kind: 'raised', id: row.dealId } : null}
      items={
        canArchiveSubmissions
          ? [
              {
                key: 'assign',
                label: row.assignment ? 'Reassign…' : 'Assign to…',
                onSelect: () => void assignSubmission(row),
                busy: assigning?.source === 'event-interest' && assigning.id === row.id,
              },
              {
                key: row.archived ? 'unarchive' : 'archive',
                label: row.archived ? 'Unarchive' : 'Archive',
                onSelect: () => void toggleSubmissionArchived(row),
                busy: archivingId === row.id,
              },
            ]
          : []
      }
      disabled={archivingId !== null}
      failure={archiveError?.id === row.id ? archiveError.message : null}
      contact={{
        email: row.contactEmail,
        canCreate: ability.canCreate('contacts'),
        onCreate: () => void createContactForSubmission(row),
        busy: creatingContact?.kind === 'submission' && creatingContact.id === row.id,
      }}
    />
  );

  return (
    <>
      <PageHeader
        eyebrow="Trackers"
        title="Enquiries"
        {...(tab === 'demand' && canRead
          ? { actions: <LayoutToolbar layout={demandLayout} /> }
          : {})}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <TabToggle
          value={tab}
          onChange={(next) => changeTab(next)}
          tabs={visibleTabs}
          counts={tabCounts}
        />
      </div>

      {/*
        ⛔ **THE REFUSAL REPLACES THE CONTENT RATHER THAN SITTING ABOVE AN EMPTY
        TABLE.** An empty table under a heading reads as "nobody has submitted",
        which is a statement about the business rather than about this reader's
        access. The ROUTE carries no guard, so a denied reader arriving by URL
        lands here — and must meet this rather than an error or a blank screen.

        ⚠️ **It is NOT the guarantee.** On a module that is not a nav surface an
        absent scope resolves READ as ALLOWED, so an unseeded catalogue, an older
        API or a failed `/permissions/me` all land on the content below and the
        SERVER refuses — which the error blocks word as a failure naming what
        came back.
      */}
      {/*
        🔴 **THE PROPOSALS TAB IS TESTED FIRST, AHEAD OF THE `event-interest`
        REFUSAL, AND THE ORDER IS THE WHOLE OF THE GATING DECISION.**

        A B2B proposal is read with `b2b-proposal.read`. That is a DIFFERENT
        grant from `event-interest.read`, and somebody may hold either without
        the other — so refusing this section on the strength of the event
        interest grant is this app answering a question nobody put to the
        server, and answering it in the module's own name. It is the
        `integrations`-borrowing-`companies` mistake exactly: a grant for one
        capability silently deciding a second nobody was asked about, except
        that here it decides in the REFUSING direction, where the symptom is a
        panel stating the wrong module's name to a reader who holds the right
        grant.

        ✅ **AND THERE IS A SLUG TO GATE IT ON.** `ModuleSlug` carries
        `'b2b-proposal'` (`src/lib/permissions.ts:521`) and `OPEN_WRITE_MODULES`
        carries it too (`:736`), so {@link B2B_PROPOSAL} asks a real question and
        this page asks it: the pill is filtered out of `visibleTabs`, and the
        branch below renders a refusal naming that module.

        ⚠️ *SUPERSEDED 2026-09-20: "⛔ **AND THERE IS NO SLUG TO GATE IT ON.
        `ModuleSlug` HAS 22 MEMBERS AND `b2b-proposal` IS NOT ONE OF THEM** …
        ⚠️ **SO THIS MOUNTS UNGATED, DELIBERATELY, AND THE SERVER IS THE
        REFUSAL** … ⚠️ **THE PILL IS SHOWN TO EVERY READER, ALWAYS.**"* **Every
        clause of that was falsified by the member landing, and the union is 23
        members now rather than 22.** The measurement was honest on the day and
        the code moved past it — **a reader meets this comment before the
        `!canReadProposals` branch a few lines down**, so the stale half is the
        half that gets believed.

        ⛔ **The prohibition it carried SURVIVES and is not spent: do not invent
        a slug for a module that has none.** An absent module resolves ALLOWED
        for `read`, so a fabricated slug is not a gate that fails closed, it is a
        gate that **degrades OPEN** — it would answer `true` for everybody,
        render as a considered permission decision to the next reader, and
        protect nothing. A fake gate is worse than none, because none is visibly
        none. **That rule is why this gate waited for the real member rather than
        being written early.**

        ⚠️ **THE CLIENT GATE IS THE OPTIMISATION AND THE SERVER IS STILL THE
        GUARANTEE** — as on this page's own module, where the comment above
        already says the client-side test is *not the guarantee*. An absent scope
        resolves READ as ALLOWED, so an unseeded catalogue, an older API or a
        failed `/permissions/me` all land on the panel and the SERVER refuses.
        ⚠️ **`ProposalsPanel` is what must word a `403` as a REFUSAL rather than
        as a failure**, or a reader without the grant meets "couldn't load" and
        goes looking for an outage. That wording is in a file this page does not
        own.

        ⚠️ **THE PILL IS WITHHELD FROM A READER WITHOUT THE GRANT, and the cost
        of that was stated rather than overlooked.** A control that disappears
        per reader reads as a control that broke — this codebase has had a
        working feature reported as DELETED when it had merely become
        unreachable — which is exactly why the URL branch below renders a
        SENTENCE rather than a redirect or a blank: a bookmark still lands
        somewhere that explains itself.

        🔴 **NEITHER BRANCH HAS EVER BEEN DRIVEN, BECAUSE THE SEPARATING
        POPULATION IS EMPTY.** Migration 219 grants `event-interest.read` to
        `university-partnerships` AND `operations`; migration 230 grants
        `b2b-proposal.read` to `university-partnerships` alone — so the reader
        who may open this page and must NOT see this pill is exactly
        `operations`, which maps to `viewer`, **which has held nobody since
        2026-09-15**. The divergence is real in the grant table and unreachable
        in the data. **Re-measure the role distribution; do not read the absence
        of complaints as correctness.**

        ✅ **ALL FOUR ROW ACTIONS ARE WIRED — the decision is taken.** ⚠️
        *SUPERSEDED: "🔴 **`headingId` IS THE ONLY PROP PASSED, SO EVERY ROW
        RENDERS READ-ONLY.** `ProposalsPanel` also accepts `onConvert`,
        `onArchive`, `onUnarchive` and `onDelete` — all optional, all withheld
        here — and it draws no control for a callback it was not given. ⚠️
        **That is an UNMADE DECISION, not a considered refusal** … **Put it to
        the owner rather than reading the absence as a ruling."***

        ⚠️ **It was put, and the ruling is to wire all four**, so the panel's
        three routes — archive/unarchive, the deal link, and the delete — are
        each supplied by the handlers above. ⛔ **Read the absence of a gate
        HERE as the panel owning every gate**, which its own header requires,
        and NOT as four controls offered to everybody: the delete is withheld
        below `isAdmin` and below an unarchived row, the convert below
        `canCreate('deals')`, and archiving is open to anyone who may read —
        three different tests, in one place, by design.
      */}
      {tab === 'proposals' && !canReadProposals ? (
        /*
         * ⛔ **A DENIED READER ARRIVING BY URL GETS A SENTENCE, NOT A REDIRECT
         * AND NOT A BLANK.** The pill is withheld, so the only way here is a
         * bookmark or a shared link — and silently moving them to another tab
         * would read as the link being broken, while a blank reads as the page
         * being broken.
         *
         * ⛔ **The panel is NOT mounted, so a denied reader issues NO REQUEST
         * FOR PROPOSALS AT ALL** — the `/b2b-service-maps` ruling. An empty
         * proposals table would say *"nobody has enquired"*, which is a claim
         * about the business rather than about this reader's grant.
         *
         * ⚠️ **It names the PROPOSALS module, never the page's** — a refusal
         * quoting `event-interest` here would send somebody to ask for a grant
         * they already hold.
         */
        <Panel>
          <EmptyState
            icon={<EventInterestIcon />}
            title="Proposals are restricted"
            description={
              denialReason(ability, 'read', B2B_PROPOSAL) ??
              'Your account does not have permission to read B2B proposal enquiries.'
            }
          />
        </Panel>
      ) : tab === 'proposals' ? (
        <ProposalsPanel
          headingId={proposalsHeadingId}
          /*
           * 2026-10-02 (owner): archive / unarchive and linking a company or a
           * deal to the enquiry are `b2b-proposal.triage`. Withheld callbacks
           * draw no control. Convert also needs `deals.create`, which the panel
           * checks; delete is `b2b-proposal.delete`, which the panel checks.
           */
          {...(canTriageProposals
            ? {
                onConvert: convertProposal,
                onArchive: archiveProposal,
                onUnarchive: unarchiveProposal,
                onCreateCompany: createCompanyForProposal,
                onAssign: assignProposal,
              }
            : {})}
          assigneeOptions={assigneeOptions}
          onDelete={deleteProposal}
          onCreateContact={createContactForProposal}
          onChanged={refreshEnquiryCounts}
        />
      ) : tab === 'reports' && !canReadReports ? (
        /*
         * ⛔ A denied reader arriving by URL gets a SENTENCE naming the
         * report-download module, and the panel is NOT mounted — so no request
         * for leads is made at all. Same ruling as the proposals branch above.
         */
        <Panel>
          <EmptyState
            icon={<EventInterestIcon />}
            title="Report leads are restricted"
            description={
              denialReason(ability, 'read', REPORT_DOWNLOAD) ??
              'Your account does not have permission to read report-download leads.'
            }
          />
        </Panel>
      ) : tab === 'reports' ? (
        <ReportDownloadsPanel
          headingId={reportsHeadingId}
          // 2026-10-02 (owner): `report-download.triage` — see the proposals panel above.
          {...(canTriageReports
            ? {
                onConvert: convertReport,
                onArchive: archiveReport,
                onUnarchive: unarchiveReport,
                onCreateCompany: createCompanyForReport,
                onAssign: assignReport,
              }
            : {})}
          assigneeOptions={assigneeOptions}
          onDelete={deleteReport}
          onCreateContact={createContactForReport}
          onChanged={refreshEnquiryCounts}
        />
      ) : !canRead ? (
        <Panel>
          <EmptyState
            icon={<EventInterestIcon />}
            title="Event interest is restricted"
            description={
              denialReason(ability, 'read', EVENT_INTEREST) ??
              'Your account does not have permission to read event interest submissions.'
            }
          />
        </Panel>
      ) : tab === 'demand' ? (
        <LayoutGrid
          layout={demandLayout}
          render={{
            'festival-demand': () => (
              <FestivalDemandPanel
                rows={festivalRows}
                loading={demandLoading}
                error={demandError}
                onOpenSubmissions={(festivalId) =>
                  changeTab('submissions', { projectId: String(festivalId) })
                }
              />
            ),
            'city-demand': () => (
              <DemandPanel
                rows={cityRows}
                loading={demandLoading}
                error={demandError}
                filtered={false}
                onClearFilters={clearFilters}
              />
            ),
          }}
        />
      ) : (
        <Panel>
          {error && (
            <div className="border-b border-border px-4 py-3">
              <Notice
                tone="danger"
                role="alert"
                title={<>Couldn&rsquo;t load event interest submissions.</>}
              >
                <span className="[overflow-wrap:anywhere]">{error}</span>
              </Notice>
            </div>
          )}

          {/* ── The view pills: Live / Archived / All ────────────────────── */}
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
            <SubmissionViewToggle
              value={submissionView}
              onChange={setSubmissionView}
              disabled={archivingId !== null}
            />
          </div>

          {/* ── The filter bank ──────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
            <div className="w-full min-w-0 sm:w-56">
              <Field label="Search event interest submissions" hideLabel>
                {({ id }) => (
                  <Input
                    id={id}
                    type="search"
                    /*
                     * ⚠️ **The placeholder names what the search MATCHES.**
                     * Promising more than the API does is a lie the results
                     * expose immediately — and one nobody reports, because an
                     * empty result reads as no matches.
                     */
                    placeholder="Search institution or contact…"
                    leadingIcon={<SearchIcon />}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                )}
              </Field>
            </div>

            <div className="w-full min-w-0 sm:w-64">
              <Field
                label="Filter by festival"
                hideLabel
                {...(demandError !== null ? { error: 'Couldn’t load festivals.' } : {})}
              >
                {({ id, 'aria-describedby': describedBy }) => (
                  <MultiCombobox
                    id={id}
                    values={filters.projectIds.map(String)}
                    onChange={(values) => setFilter('projectId', values.join(','))}
                    options={festivalOptions}
                    summaryNoun={{ singular: 'festival', plural: 'festivals' }}
                    placeholder="All festivals"
                    searchPlaceholder="Search festivals…"
                    loading={demandLoading}
                    aria-describedby={describedBy}
                    {...(demandError !== null ? { invalid: true } : {})}
                  />
                )}
              </Field>
            </div>

            {/*
              ⚠️ **THE LIST'S HALF OF THE UNAPPROVED SPLIT.** A submission
              attaches before its company is approved, so this is what answers
              *"which institutions am I waiting on"* rather than leaving a
              reader to scan the badges. ⚠️ **`rejected` is offered like any
              other**: those submissions survive deliberately, and an option
              nobody can pick makes a real population unreachable.
            */}
            <div className="w-full min-w-0 sm:w-52">
              <Field label="Filter by approval" hideLabel>
                {({ id }) => (
                  <MultiCombobox
                    id={id}
                    values={filters.approvalStatuses}
                    onChange={(values) => setFilter('approvalStatus', values.join(','))}
                    options={APPROVAL_FILTER_OPTIONS}
                    summaryNoun={{ singular: 'approval state', plural: 'approval states' }}
                    placeholder="Any approval state"
                  />
                )}
              </Field>
            </div>

            {anyFilter && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear
              </Button>
            )}

            {/* Only the `md`+ table has columns to manage; the cards carry every field. */}
            <div className="hidden md:ml-auto md:block">
              <ColumnManager
                layout={layout}
                labelOf={labelOf}
                lockedKeys={LOCKED_KEYS}
                lockReasonOf={lockReasonOf}
                pinnedKeys={PINNED_KEYS}
              />
            </div>
          </div>

          {/*
            `companyId` is arrived at BY LINK — from the company record's own
            panel — and has no control of its own, so it gets a badge. A
            filtered list must never look unfiltered.
          */}
          {filters.companyId !== null && (
            <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2 text-xs text-fg-muted">
              <span>Also filtered by</span>
              <Badge tone="neutral" variant="outline">
                Company #{filters.companyId}
              </Badge>
            </div>
          )}

          <div
            aria-busy={loading || undefined}
            className={cx(
              'transition-opacity duration-150 ease-out motion-reduce:transition-none',
              loading && result && 'opacity-60',
            )}
          >
            {/* ── Below `md`: a stacked card per submission ───────────────
                Six columns will not fit 320px. The card carries every one of
                them: a column that exists only above a breakpoint is
                indistinguishable from one that was removed. */}
            <div className="md:hidden">
              {/*
                A card has no header row, so the funnel would have nowhere to
                hang below `md`. `FilterSheet` is its mobile form, handed the
                SAME `headerFor` and `clearAll` the table headers use — one
                state store rather than two that can disagree.
              */}
              <div className="border-b border-border px-4 py-2">
                <FilterSheet
                  keys={COLUMN_KEYS}
                  headerFor={headerFor}
                  activeCount={table.activeCount}
                  clearAll={table.clearAll}
                />
              </div>

              {initialLoad ? (
                <ul className="m-0 list-none p-0">
                  {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                    <li key={index} className="border-b border-border px-4 py-3 last:border-b-0">
                      <div className="flex flex-col gap-2">
                        <Skeleton className="h-3 w-44" />
                        <Skeleton className="h-2.5 w-28" />
                        <Skeleton className="h-2.5 w-36" />
                      </div>
                    </li>
                  ))}
                </ul>
              ) : visibleRows.length === 0 ? (
                emptyState
              ) : (
                <ul className="m-0 list-none p-0">
                  {visibleRows.map((row) => (
                    <li key={row.id} className="border-b border-border px-4 py-3 last:border-b-0">
                      {/* The Assigned column leads the table, so it leads the card. */}
                      <div className="mb-2 min-w-0">
                        <EnquiryAssignmentCell assignment={row.assignment} />
                      </div>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
                            {row.companyName}
                          </p>
                          <p className="m-0 mt-0.5 text-xs text-fg-muted [overflow-wrap:anywhere]">
                            {row.contactName} ·{' '}
                            <EmailLink address={row.contactEmail} tone="muted" />
                          </p>
                        </div>
                        {row.approvalStatus !== 'approved' && (
                          <Badge tone={row.approvalStatus === 'pending' ? 'warning' : 'neutral'}>
                            {APPROVAL_LABEL[row.approvalStatus]}
                          </Badge>
                        )}
                      </div>

                      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        <dt className="m-0 text-fg-muted">{COLUMNS[REGION_COLUMN].label}</dt>
                        <dd className="m-0 text-right text-fg-secondary">
                          {formRegionLabel(row.formRegion)}
                        </dd>
                        <dt className="m-0 text-fg-muted">{COLUMNS[ANSWERED_COLUMN].label}</dt>
                        <dd className="m-0 text-right text-fg-secondary tabular">{row.answered}</dd>
                        <dt className="m-0 text-fg-muted">{COLUMNS[SAID_YES_COLUMN].label}</dt>
                        <dd className="m-0 text-right text-fg-secondary tabular">{row.saidYes}</dd>
                      </dl>

                      <p className="mt-1.5 m-0 text-xs text-fg-muted tabular">
                        Submitted {formatDateTime(row.submittedAt)}
                      </p>
                      {row.archived && (
                        <p className="m-0 mt-1">
                          <Badge tone="neutral">Archived</Badge>
                        </p>
                      )}
                      <div className="mt-2">
                        <Button variant="secondary" size="sm" onClick={() => setOpenId(row.id)}>
                          See the answers
                        </Button>
                      </div>
                      {/* The same Actions slots the table's pinned column carries —
                          a control that exists only above `md` reads as removed. */}
                      <div className="mt-2 border-t border-border pt-2">
                        {submissionActions(row)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* ── From `md`: the table ────────────────────────────────────
                ONE mount, with only the `<TBody>` swapping. Two branches would
                mean two header rows and two measured layouts. */}
            <div className="hidden md:block">
              {!initialLoad && visibleRows.length === 0 ? (
                emptyState
              ) : (
                <TableWrap frozenHeader>
                  {/* The floor goes through `style`: Tailwind v4 scans source
                      text, so an interpolated `min-w-[…]` emits no rule at
                      all — the class lands in the DOM and does nothing. */}
                  <Table style={{ minWidth: tableMinWidth }}>
                    <THead>
                      <TR ref={headRowRef}>
                        {/* Rendered in `layout.visibleKeys` order; Actions is pinned
                            (`sticky right-0`), so it stays LAST. */}
                        {layout.visibleKeys.map((key) => {
                          const side = layout.frozenSideOf(key);
                          if (isColumnKey(key)) {
                            return (
                              <FilterableTH
                                key={key}
                                {...headerFor(key)}
                                frozen={side}
                                frozenOffset={frozenOffsets[key]}
                              />
                            );
                          }
                          if (key === ACTIONS_KEY) {
                            return (
                              <TH key={key} pinned style={{ width: ACTIONS_COLUMN_WIDTH }}>
                                Actions
                              </TH>
                            );
                          }
                          return (
                            <TH key={key} frozen={side} frozenOffset={frozenOffsets[key]}>
                              <span className="sr-only">Answers</span>
                            </TH>
                          );
                        })}
                      </TR>
                    </THead>

                    {initialLoad ? (
                      <TBody>
                        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                          <TR key={index}>
                            {layout.visibleKeys.map((key) => {
                              const side = layout.frozenSideOf(key);
                              if (isColumnKey(key)) {
                                return (
                                  <TD
                                    key={key}
                                    numeric={COLUMNS[key].numeric}
                                    frozen={side}
                                    frozenOffset={frozenOffsets[key]}
                                  >
                                    <SkeletonCellContent columnKey={key} />
                                  </TD>
                                );
                              }
                              if (key === ACTIONS_KEY) {
                                return (
                                  <TD key={key} pinned>
                                    <div className="flex flex-col gap-1.5">
                                      <Skeleton className="h-3 w-28" />
                                      <Skeleton className="h-2.5 w-16" />
                                    </div>
                                  </TD>
                                );
                              }
                              return (
                                <TD key={key} frozen={side} frozenOffset={frozenOffsets[key]}>
                                  <Skeleton className="h-3 w-24" />
                                </TD>
                              );
                            })}
                          </TR>
                        ))}
                      </TBody>
                    ) : (
                      <TBody>
                        {visibleRows.map((row) => (
                          <TR key={row.id}>
                            {layout.visibleKeys.map((key) => {
                              const side = layout.frozenSideOf(key);
                              if (isColumnKey(key)) {
                                return (
                                  <TD
                                    key={key}
                                    numeric={COLUMNS[key].numeric}
                                    verticalAlign="top"
                                    frozen={side}
                                    frozenOffset={frozenOffsets[key]}
                                  >
                                    <CellContent columnKey={key} row={row} />
                                  </TD>
                                );
                              }
                              if (key === ACTIONS_KEY) {
                                return (
                                  <TD key={key} pinned verticalAlign="top">
                                    {submissionActions(row)}
                                  </TD>
                                );
                              }
                              return (
                                <TD
                                  key={key}
                                  verticalAlign="top"
                                  frozen={side}
                                  frozenOffset={frozenOffsets[key]}
                                >
                                  {/*
                                    ⛔ **A BUTTON, NOT A LINK.** `App.tsx` declares
                                    no `/event-interest/:id`, so a link would land
                                    on `NotFound` — which reads as the record being
                                    missing rather than as the page not existing.
                                  */}
                                  <Button variant="ghost" size="sm" onClick={() => setOpenId(row.id)}>
                                    See the answers
                                  </Button>
                                </TD>
                              );
                            })}
                          </TR>
                        ))}
                      </TBody>
                    )}
                  </Table>
                </TableWrap>
              )}
            </div>
          </div>

          {result && (
            <PanelFooter>
              <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
                {total === 0
                  ? `No ${submissionViewSpec.plural} to show`
                  : `Showing ${firstRow}–${lastRow} of ${total} ${
                      total === 1 ? submissionViewSpec.singular : submissionViewSpec.plural
                    }`}
              </p>
              <div className="flex items-center gap-2">
                <span className="hidden text-xs text-fg-subtle tabular sm:inline">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex-1 sm:flex-none"
                  leadingIcon={<ChevronIcon direction="left" />}
                  disabled={page <= 1}
                  onClick={() => setPage((current) => current - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex-1 sm:flex-none"
                  trailingIcon={<ChevronIcon direction="right" />}
                  disabled={page >= totalPages}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                </Button>
              </div>
            </PanelFooter>
          )}
        </Panel>
      )}

      <SubmissionDetailModal
        open={openId !== null}
        onClose={() => setOpenId(null)}
        submission={detail}
        loading={detailLoading}
        error={detailError}
        /*
         * ⚠️ **THE CONTROL SELF-GATES TOO — `canDeleteSubmissions` is asked here
         * only so the FOOTER is not drawn as an empty strip** for a reader who
         * will never see a button in it. One predicate, two questions: *may
         * this reader delete* and *is there anything to put in the footer*.
         * ⛔ Do not answer the second with `ability.isAdmin` written out again;
         * two spellings of one rule is how a destructive control comes to
         * appear on one surface and not the other.
         *
         * ⚠️ **The dialog itself withholds this over skeletons and over a
         * failed read**, so nothing here needs to test `detail` — see
         * `SubmissionDetailModalProps.actions`.
         */
        {...(canDeleteSubmissions(ability) && detail !== null
          ? {
              actions: (
                <DeleteSubmissionControl
                  submissionId={detail.id}
                  institution={detail.companyName}
                  submittedAt={detail.submittedAt}
                  projects={removedProjects}
                  onDeleted={handleSubmissionDeleted}
                />
              ),
            }
          : {})}
      />

      {/*
        🔴 **ONE MOUNT, AT THE PAGE, AND DELIBERATELY OUTSIDE THE TAB BRANCH.**
        It mounts two large modals with their own hooks and their own
        rate-limited reads, so one per row would be one per row. ⚠️ **Outside
        the branch so a tab switch mid-flow does not unmount a company that is
        half-created** — this sits beside `SubmissionDetailModal` for the same
        reason, and it costs nothing on the other two tabs because `converting`
        can only be set from the proposals one.

        ⚠️ **MOUNTED ONLY WHEN THERE IS AN ENQUIRY, AND `open` IS THEREFORE
        HARD-`true`.** `submission` is REQUIRED and not nullable, so there is no
        honest way to hold this mounted and empty; unmounting is also what
        re-arms its own on-open effect for the next conversion.

        ⛔ **`onRecordDeal` IS THE ONLY WRITER OF THIS ENQUIRY'S `dealId` ON THIS
        PAGE** — see {@link recordDeal} for why that is structural rather than a
        convention, and for the two refusal codes neither of us words.

        ⚠️ **`onChanged` IS DELIBERATELY NOT PASSED.** It fires just before
        `onClose` when something was written, and `onClose` already settles the
        promise the panel is holding, which re-reads the list. Passing both
        would be two triggers for one reload, and this page holds no other
        proposal state to refresh — the demand report and the submissions list
        are the festival form, and nothing is shared between them.
      */}
      {converting !== null && (
        <ProposalConvertFlow
          open
          submission={converting}
          onClose={closeConvert}
          onRecordDeal={(dealId) => recordDeal(converting.id, dealId)}
        />
      )}

      {/*
        The create-company modal — ONE mount, at the page, outside the tab
        branch, shared by the proposals and report-lead sections. `onClose`
        settles the promise the panel is holding, which is what re-reads that
        panel's list; `onLinked` refreshes the page-level enquiry counts.
      */}
      <CreateCompanyForEnquiry
        open={creatingCompany !== null}
        seed={creatingCompany?.seed ?? null}
        onClose={closeCreateCompany}
        link={linkCreatedCompany}
        onLinked={() => {
          // The modal does not close itself after an attach — this does, and
          // settling the panel's promise is what makes it re-read its list.
          refreshEnquiryCounts();
          closeCreateCompany();
        }}
      />

      {/*
        The create-contact flow (owner, 2026-10-02) — ONE mount, at the page,
        shared by all three tabs. ⚠️ `onLinked` does NOT close: the contact form
        follows the link. `onCreated` closes, which settles the panel's promise.
      */}
      <CreateContactForEnquiry
        open={creatingContact !== null}
        request={creatingContact?.request ?? null}
        onClose={closeCreateContact}
        link={linkContactCompany}
        onLinked={() => refreshEnquiryCounts()}
        onCreated={() => closeCreateContact()}
      />

      {/*
        The assign modal — ONE mount, at the page, outside the tab branch,
        shared by all three tabs. Mounted only while open, so its own state
        starts fresh for each enquiry. Closing settles the panel's promise,
        which re-reads that panel's list and refreshes the counts; the
        submissions tab is reloaded here because it owns no promise.
      */}
      {assigning !== null && (
        <AssignEnquiryModal
          open
          source={assigning.source}
          enquiryId={assigning.id}
          enquiryName={assigning.name}
          current={assigning.current}
          onClose={closeAssign}
          onDone={() => {
            if (assigning.source === 'event-interest') {
              void load();
              refreshEnquiryCounts();
            }
            closeAssign();
          }}
        />
      )}

      {/* The report-download convert flow — one mount, at the page, outside the tab branch. */}
      {convertingReport !== null && (
        <ReportDownloadConvertFlow
          open
          lead={convertingReport}
          onClose={closeReportConvert}
          onRecordDeal={(dealId) => recordReportDeal(convertingReport.id, dealId)}
        />
      )}
    </>
  );
}

/**
 * ⚠️ **`role="group"` with `aria-pressed`, NOT `role="tab"`.** These switch what
 * the section below IS rather than labelling panels that share one tabstop,
 * matching every other page-level view switch in this app. A real tablist would
 * promise arrow-key navigation between panels that do not share a tabstop.
 *
 * ⚠️ **Every pill renders at every width, always.** This codebase has had a
 * working feature reported as *deleted* when it had merely become unreachable
 * behind a toggle. The selected pill is not distinguished by colour alone:
 * `secondary` gives it a raised surface against `ghost`'s transparent one, and
 * `aria-pressed` states it outright.
 *
 * 🔴 **`shrink-0` WAS REMOVED WHEN THE THIRD PILL LANDED, AND IT IS NOT
 * COSMETIC.** It sat beside this strip's own `flex-wrap`, which made that wrap
 * DEAD: a flex item with `flex-shrink: 0` never becomes narrower than its
 * content, so its children never need a second row. The pills simply stayed on
 * one line and the overflow was clipped by an ancestor — present in the DOM, at
 * the right position, reachable by nothing, and with no document overflow for a
 * page to report. `/settings` shipped precisely that with four pills, its last
 * one measured at x=344 on a 320 viewport.
 *
 * ⚠️ **TWO PILLS FIT 320 AND THREE ARE BORDERLINE**, which is exactly the width
 * at which a dead guard stops being harmless — so the wrap is made real now
 * rather than at whatever pill count would have made the clipping visible.
 * ⛔ **Do not copy a `shrink-0` back in from `CommissionsPage`**, which is right
 * to carry one: it holds two pills and SHARES ITS ROW with other controls, so
 * it must not be squeezed. This strip is alone on its row and the parent's own
 * `flex-wrap` is what keeps it off anything else.
 *
 * ⚠️ **UNMEASURED: nothing has been driven at 320.** The reasoning above is
 * read off the box model, not off a browser.
 */
function TabToggle({
  value,
  onChange,
  tabs,
  counts,
}: {
  value: Tab;
  onChange: (next: Tab) => void;
  /*
   * ⚠️ **THE VISIBLE TABS, NOT ALL OF THEM** — the Proposals pill is withheld
   * from a reader with no `b2b-proposal.read` grant, because this page holds two
   * modules' data. ⛔ **A pill that is rendered and then refuses would spend a
   * request a denied reader need not make**, which is the `/b2b-service-maps`
   * ruling; and it would put a second module's refusal inside a page the reader
   * is otherwise entitled to.
   *
   * ⚠️ **The counter-hazard is real and is why a URL arrival still gets a
   * SENTENCE rather than a redirect**: this repo has had a merely-unreachable
   * control reported by the owner as a DELETED feature.
   */
  tabs: readonly { value: Tab; label: string }[];
  /*
   * The to-deal-with count per pill. ⚠️ **Demand is deliberately ABSENT** — it
   * is a report, not a queue, so it carries no badge. `null` (not read, or the
   * read failed) and `0` both draw nothing, and neither adds to the name.
   */
  counts: Partial<Record<Tab, number | null>>;
}) {
  return (
    <div
      role="group"
      aria-label="Enquiries view"
      /* ⚠️ `max-w-full` and NO `shrink-0` / `whitespace-nowrap` — the wrap must
         stay live, or the last pill is clipped at 320 (the /settings defect). */
      className="inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-md border border-border bg-surface-sunken p-0.5"
    >
      {tabs.map((item) => {
        const count = item.value === 'demand' ? null : (counts[item.value] ?? null);
        const hasCount = typeof count === 'number' && count > 0;
        return (
          <Button
            key={item.value}
            size="sm"
            variant={value === item.value ? 'secondary' : 'ghost'}
            aria-pressed={value === item.value}
            {...(hasCount
              ? {
                  // Event interest counts every live submission (owner, 2026-10-13);
                  // the other two count what is still to deal with.
                  'aria-label':
                    item.value === 'submissions'
                      ? `${item.label}, ${count} live`
                      : `${item.label}, ${count} to deal with`,
                }
              : {})}
            onClick={() => onChange(item.value)}
          >
            {item.label}
            {item.value !== 'demand' && <CountBadge count={count} />}
          </Button>
        );
      })}
    </div>
  );
}

/**
 * The Live / Archived / All pills for the submissions tab — the same shape as
 * `ProposalsPanel`'s view toggle: `role="group"` with `aria-pressed`, and
 * `max-w-full` with NO `shrink-0` so the wrap stays live at 320.
 */
function SubmissionViewToggle({
  value,
  onChange,
  disabled,
}: {
  value: SubmissionView;
  onChange: (next: SubmissionView) => void;
  disabled: boolean;
}) {
  return (
    <div
      role="group"
      aria-label="Event interest submissions shown"
      className="inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-md border border-border bg-surface-sunken p-0.5"
    >
      {SUBMISSION_VIEWS.map((spec) => (
        <Button
          key={spec.value}
          size="sm"
          variant={value === spec.value ? 'secondary' : 'ghost'}
          aria-pressed={value === spec.value}
          disabled={disabled}
          onClick={() => onChange(spec.value)}
        >
          {spec.label}
        </Button>
      ))}
    </div>
  );
}

/**
 * The empty state — three of them, because "nothing came back" has three
 * causes and only one is the reader's to act on.
 *
 * ⛔ **NO COPY HERE IMPLIES DATA IS ON ITS WAY.** A blank list is an ordinary,
 * permanent state, and an empty state that hedges about it is how a dead
 * endpoint gets mistaken for a quiet week — `GET /deliverables` returned a
 * `500` for a day and rendered exactly its documented empty state.
 */
function SubmissionsEmptyState({
  filtered,
  hasError,
  onClear,
  view,
}: {
  filtered: boolean;
  hasError: boolean;
  onClear: () => void;
  /** The view being shown — the unfiltered empty state must name it. */
  view: SubmissionViewSpec;
}): ReactNode {
  if (hasError) {
    return (
      <EmptyState
        icon={<EventInterestIcon />}
        title="Nothing to show"
        /* Points at the banner above rather than restating the server's
           message — one copy of a refusal, where the whole of it is. */
        description="Event interest submissions couldn’t be loaded. Check the message above and try again."
      />
    );
  }

  return (
    <EmptyState
      icon={<EventInterestIcon />}
      title={filtered ? `No ${view.singular} matches those filters` : view.emptyTitle}
      description={
        filtered
          ? `Clear the filters to see every ${view.singular} in this view.`
          : view.emptyDescription
      }
      action={
        filtered ? (
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        ) : null
      }
    />
  );
}
